import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  PauseCircleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  SignalIcon,
  XCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import InteractiveTroubleshooting from '../components/InteractiveTroubleshooting';
import TVTroubleshooting from '../components/TVTroubleshooting';

// Declare AirmaxChat global type
declare global {
  interface Window {
    AirmaxChat?: {
      open?: () => void;
      show?: () => void;
      toggle?: () => void;
      hide?: () => void;
      hideGreetingBubble?: () => void;
      init: (config: { companyId: number }) => void;
      isOpen?: boolean;
      greetingBubbleShown?: boolean;
    };
  }
}

const ADMIN_PASSWORD = 'airmax2024admin';

type FaqItem = { question: string; answer: string };

/** Turn plain-text FAQ answers into nodes with clickable http(s) links. */
const renderFaqAnswer = (answer: string) => {
  const parts = answer.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const href = part.replace(/[.,;:!?)\]}>]+$/, '');
      const trailing = part.slice(href.length);
      const external = !href.includes('airmaxcm.com');
      return (
        <React.Fragment key={i}>
          <a
            href={href}
            className="text-airmax-blue font-semibold underline break-all"
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
          >
            {href}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const DEFAULT_FAQS: FaqItem[] = [
  {
    question: 'How do I reboot my router?',
    answer:
      'Unplug your router from power, wait 5 seconds, then plug it back in. Wait 2-3 minutes for it to fully restart. If issues persist after power cycling, please contact support for assistance.',
  },
  {
    question: 'Why is my internet slow?',
    answer:
      'Several things can slow your connection: distance from your router, too many devices online, network congestion, or weather on a wireless link. First, restart your router — unplug it for 30 seconds, plug it back in, and wait 2–3 minutes. Then move closer to the router and pause big downloads or streaming on other devices. If it is still slow, use Interactive Connection Troubleshooting on this Support page, or call us at 928-565-6707 (Monday–Friday, 9 AM – 5 PM Arizona Time).',
  },
  {
    question: 'What should I do if I have no internet connection?',
    answer:
      'First, check if other devices are affected. If yes, restart your router and check all cable connections. If the issue persists, check our service status page or contact our support team.',
  },
  {
    question: 'How can I improve my Wi-Fi signal?',
    answer:
      'Place your router in a central, elevated location away from walls and metal objects. Avoid placing it near microwaves or cordless phones. Consider a Wi-Fi extender for larger homes.',
  },
  {
    question: 'What do the different lights on my router mean?',
    answer:
      'Power light should be solid green. Internet/WAN light should be solid or blinking when connected. Wi-Fi light indicates wireless network is active. Red or orange lights typically indicate a problem.',
  },
  {
    question: 'How do I update my payment information?',
    answer:
      'Log into your customer portal at https://www.airmaxbilling.com/crm/login, navigate to Account Settings, then Payment Methods. You can add a new card or update existing information there.',
  },
  {
    question: 'How do I reset my billing account password?',
    answer:
      'Use the billing portal password reset at https://www.airmaxbilling.com/crm/reset-password. Enter your username (usually your email) and follow the instructions. This resets your billing portal login only — it does not change your WiFi or router password.',
  },
  {
    question: 'Can I extend my bill due date?',
    answer:
      'Yes. If you need more time to pay, use Payment Extension: go to https://airmaxcm.com/payment-extension, verify with the phone number on your account, pick an unpaid invoice, and extend the due date. Eligible invoices can usually be extended one time. You can also open it from the Support page under “Extend your bill due date.”',
  },
  {
    question: 'Can I change my service plan?',
    answer:
      'Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle. Contact our support team or use the customer portal.',
  },
  {
    question: 'What happens during weather, power outages, or storms?',
    answer:
      'When weather, power outages, or major storms affect service, our SMS alert system texts you when the network goes down and again when service is restored. In most cases, service is back within 10–20 minutes. If it is not restored after that, call support at 928-565-6707.\n\nYou do not need to change or reset any of your equipment while you wait. Only if service is still down after about 30 minutes should you reboot your outdoor antenna and indoor router, following the step-by-step guide here: https://airmaxcm.com/support#interactive-troubleshooting',
  },
];

type AssistantReply = {
  title: string;
  /** Plain chat replies (greetings / no match) */
  message?: string;
  /** Short lead-in under the title */
  intro?: string;
  /** One short paragraph — causes / context */
  summary?: string;
  /** Numbered actions, rendered with real layout */
  steps?: string[];
  suggestions?: string[];
  /** Optional deep-link / tool (payment extension, interactive guide, etc.) */
  actionUrl?: string;
  actionLabel?: string;
  elementId?: string;
};

const getChatStyleReply = (query: string): AssistantReply | null => {
  const q = query.trim().toLowerCase().replace(/[!?.]+$/g, '').trim();
  if (!q) return null;

  const greetings = [
    'hi', 'hello', 'hey', 'hiya', 'howdy', 'good morning', 'good afternoon',
    'good evening', 'hello there', 'hi there', 'hey there', 'hola',
  ];
  if (greetings.includes(q) || (/^(hi|hello|hey)\b/.test(q) && q.split(/\s+/).length <= 4)) {
    return {
      title: 'Hello!',
      message:
        'Hi — I can help with internet, WiFi, router, TV, billing, and more.\n\nType your problem in plain words (for example: “no internet” or “slow wifi”), or tap a suggestion below.',
      suggestions: ['no internet', 'slow wifi', 'reboot router', 'extend bill', 'billing'],
    };
  }

  if (/^(thanks|thank you|thx|ty)\b/.test(q)) {
    return {
      title: 'You’re welcome',
      message:
        'Glad to help. If something else comes up, just type it here — or call us at 928-565-6707 Monday–Friday, 9 AM – 5 PM (Arizona Time). Online Support Center is available 24/7.',
    };
  }

  if (
    /\b(talk to (a )?human|speak to (a )?person|real person|customer service|call (me|someone)|need (a )?human)\b/.test(q) ||
    q === 'help' ||
    q === 'i need help'
  ) {
    return {
      title: 'We can help',
      message:
        'You can keep typing your issue here and I’ll try to find steps, or call Airmax at 928-565-6707 Monday–Friday, 9 AM – 5 PM (Arizona Time).\n\nOnline self-help is available anytime on this Support Center page.',
      suggestions: ['no internet', 'router lights', 'extend bill', 'billing', 'network status'],
    };
  }

  if (/^(ok|okay|yes|no|sure|please|help me)\b/.test(q) && q.split(/\s+/).length <= 3) {
    return {
      title: 'What do you need help with?',
      message:
        'Tell me what’s wrong in a short sentence — like “internet not working”, “extend my bill”, or “TV won’t connect” — and I’ll look it up for you.',
      suggestions: ['no internet', 'slow internet', 'extend bill due date', 'smart tv'],
    };
  }

  return null;
};

const noMatchReply = (query: string): AssistantReply => ({
  title: 'I couldn’t find that',
  message: `I looked for “${query.trim()}” but didn’t find a matching guide.\n\nTry different words (router, wifi, slow, TV, billing, extend bill), or call us at 928-565-6707 Monday–Friday, 9 AM – 5 PM (Arizona Time). Online Support Center is available 24/7.`,
  suggestions: ['no internet', 'check antenna', 'extend bill', 'billing', 'outage'],
});

/** Keep assistant answers on Airmax Support content — no third-party help sites. */
const scrubOffSiteHelp = (text: string): string =>
  text
    .replace(/\s*[,.]?\s*then run a speed test at speedtest\.net\.?/gi, '.')
    .replace(/run a speed test at speedtest\.net\.?/gi, 'restart your router')
    .replace(/https?:\/\/(?:www\.)?speedtest\.net\/?/gi, '')
    .replace(/\bspeedtest\.net\b/gi, '')
    // Collapse spaces/tabs only — never wipe newlines (that crushes the layout)
    .replace(/[^\S\n]{2,}/g, ' ')
    .replace(/\.\s*\./g, '.')
    .trim();

/** Pull “1) … 2) …” or newline lists out of a blob so we can lay them out. */
const splitAnswerParts = (raw: string): { summary: string; steps: string[] } => {
  const text = scrubOffSiteHelp(raw);
  const steps: string[] = [];

  // Prefer explicit numbered steps: 1) … 2) …
  const numbered = text.match(/(?:^|\n)\s*\d+[.)]\s+.+/g);
  if (numbered && numbered.length >= 2) {
    const firstIdx = text.search(/\d+[.)]\s+/);
    const summary = firstIdx > 0 ? text.slice(0, firstIdx).replace(/\n+/g, ' ').trim() : '';
    numbered.forEach((line) => {
      const cleaned = line.replace(/^\s*\d+[.)]\s+/, '').trim();
      if (cleaned) steps.push(cleaned);
    });
    return { summary: summary.replace(/^From Slow Speeds on this page:\s*/i, '').trim(), steps };
  }

  // Or line-broken bullets
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const bulletLike = lines.filter((l) => /^[-•*]|\d+[.)]/.test(l));
    if (bulletLike.length >= 2) {
      const summaryLines = lines.filter((l) => !/^[-•*]|\d+[.)]/.test(l));
      return {
        summary: summaryLines.join(' ').trim(),
        steps: bulletLike.map((l) => l.replace(/^[-•*]|\d+[.)]\s*/, '').trim()),
      };
    }
    // First line/paragraph as summary, rest as soft steps if short lines
    if (lines.length >= 3 && lines.slice(1).every((l) => l.length < 120)) {
      return { summary: lines[0], steps: lines.slice(1) };
    }
  }

  return { summary: text.replace(/\n+/g, '\n\n').trim(), steps: [] };
};

/** Turn a knowledge hit into one spoken answer (not a results list). */
const hitToChatReply = (
  query: string,
  hit: {
    type: string;
    title: string;
    content: string;
    elementId?: string;
    url?: string;
  }
): AssistantReply => {
  const q = query.trim();
  const url = hit.url || '';
  const isSupportLocal = !url || url === '/support' || url === '/the';
  const isMarketing =
    url === '/' ||
    url === '/plans' ||
    url === '/about' ||
    url === '/gamers' ||
    url === '/how-it-works';

  let actionUrl: string | undefined;
  let actionLabel: string | undefined;
  if (url && !isSupportLocal && !isMarketing) {
    actionUrl = url;
    if (url.includes('payment-extension')) actionLabel = 'Open Payment Extension';
    else if (url.includes('pay-bill')) actionLabel = 'Pay your bill';
    else if (url.includes('reset-password')) actionLabel = 'Reset Billing Password';
    else if (url.includes('tower-status')) actionLabel = 'Check network status';
    else if (url.includes('contact')) actionLabel = 'Open Contact';
    else if (url.includes('trendmicro') || url.includes('housecall')) actionLabel = 'Open Free Virus Scanner';
    else if (url.startsWith('http') && !/airmax/i.test(url) && !/trendmicro/i.test(url)) {
      actionUrl = undefined;
    } else if (url.startsWith('http')) actionLabel = 'Open link';
    else actionLabel = 'Open this page';
  } else if (hit.elementId === 'interactive') {
    actionLabel = 'Open Interactive Troubleshooting (with pictures)';
  } else if (hit.elementId === 'antenna-status') {
    actionLabel = 'Check your antenna status';
  } else if (hit.elementId === 'tv-troubleshooting') {
    actionLabel = 'Open Smart TV Troubleshooting';
  } else if (hit.elementId === 'troubleshooting' || hit.elementId?.startsWith('faq-')) {
    actionLabel = 'Show this guide on the page';
  }

  const suggestions =
    /\b(tv|television|streaming|netflix)\b/i.test(q + hit.title)
      ? ['reboot router', 'no internet', 'wifi signal', 'slow internet']
      : /\b(antenna|signal)\b/i.test(q + hit.title)
        ? ['no internet', 'reboot router', 'outage', 'slow internet']
      : /\bslow|speed\b/i.test(q + hit.title + hit.content)
      ? ['reboot router', 'no internet', 'wifi signal', 'network status']
      : /\b(extend|bill|payment|invoice)\b/i.test(q + hit.title)
        ? ['pay bill', 'update payment card', 'billing hours']
        : ['no internet', 'check antenna', 'tv not working', 'reboot router'];

  const { summary, steps } = splitAnswerParts(hit.content);

  return {
    title: hit.title,
    intro: `From our Support Center — for “${q}”:`,
    summary: summary || undefined,
    steps: steps.length > 0 ? steps : undefined,
    // Fallback if we couldn't structure it
    ...(!summary && steps.length === 0
      ? { message: scrubOffSiteHelp(hit.content) }
      : {}),
    suggestions,
    ...(actionUrl ? { actionUrl, actionLabel } : {}),
    ...(!actionUrl && hit.elementId
      ? { elementId: hit.elementId, actionLabel: actionLabel || 'Show me on this page' }
      : {}),
  };
};

type EmAntenna = {
  found: boolean;
  status: string;
  statusLabel: string;
  isOnline: boolean | null;
  signal: number | null;
  lastSeen: string | null;
  model: string | null;
};

type EmStatus = {
  businessHours: boolean;
  feeMax: number;
  responseHours: number;
  phoneSupport: string;
  hoursLabel: string;
  network: {
    ok: boolean;
    outage: boolean;
    affectsAccount?: boolean;
    found?: boolean;
    matchedName?: string | null;
    activeCount?: number;
    online: number;
    offline: number;
    total: number;
    message: string;
    antenna?: EmAntenna | null;
  };
};

const formatAntennaLastSeen = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
};

const AntennaStatusCard: React.FC<{ antenna?: EmAntenna | null; matchedName?: string | null }> = ({
  antenna,
  matchedName,
}) => {
  if (!antenna) return null;
  const st = String(antenna.status || 'unknown');
  const tone =
    st === 'online'
      ? 'border-green-200 bg-green-50 text-green-950'
      : st === 'offline'
        ? 'border-red-200 bg-red-50 text-red-950'
        : st === 'degraded'
          ? 'border-amber-200 bg-amber-50 text-amber-950'
          : 'border-gray-200 bg-white text-gray-800';
  const iconWrap =
    st === 'online'
      ? 'bg-green-600 text-white'
      : st === 'offline'
        ? 'bg-red-600 text-white'
        : st === 'degraded'
          ? 'bg-amber-500 text-white'
          : 'bg-gray-500 text-white';
  const StatusIcon =
    st === 'online'
      ? CheckCircleIcon
      : st === 'offline'
        ? XCircleIcon
        : st === 'degraded'
          ? ExclamationTriangleIcon
          : SignalIcon;
  const badge =
    st === 'online' ? 'Online' : st === 'offline' ? 'Offline' : st === 'degraded' ? 'Weak link' : 'Unknown';
  const last = formatAntennaLastSeen(antenna.lastSeen);
  return (
    <div className={`rounded-xl border px-4 py-3.5 text-sm ${tone}`}>
      <div className="flex items-start gap-3">
        <span
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${iconWrap}`}
          aria-hidden="true"
        >
          <StatusIcon className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-semibold text-base">Your outdoor antenna: {badge}</p>
          <p>{antenna.statusLabel}</p>
          {matchedName ? <p className="text-xs opacity-80">Account: {matchedName}</p> : null}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90 pt-0.5">
            {last ? <span>Last seen: {last}</span> : null}
            {antenna.model ? <span>Model: {antenna.model}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Quick antenna check — expands from the same Support shortcut card (no separate section). */
const AntennaStatusChecker: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [matchedName, setMatchedName] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [outage, setOutage] = useState(false);
  const [antenna, setAntenna] = useState<EmAntenna | null>(null);
  const [checked, setChecked] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPanel = (scroll = false) => {
    setOpen(true);
    window.AirmaxVisitor?.note('Opened Check Antenna Status from Support');
    if (scroll && triggerRef.current) {
      triggerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    window.setTimeout(() => inputRef.current?.focus(), 80);
  };

  const closePanel = () => {
    setOpen(false);
    setError('');
    if (typeof window !== 'undefined' && window.location.hash === '#antenna-status') {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  };

  useEffect(() => {
    const onOpen = () => openPanel(true);
    window.addEventListener('airmax-open-antenna-check', onOpen);
    // Never auto-open from URL hash — only when the user taps the card (or search CTA).
    if (typeof window !== 'undefined' && window.location.hash === '#antenna-status') {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
    return () => window.removeEventListener('airmax-open-antenna-check', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const check = async () => {
    setError('');
    setChecked(false);
    setAntenna(null);
    setMatchedName(null);
    setMessage('');
    setOutage(false);
    const name = accountName.trim();
    if (name.length < 2) {
      setError('Enter the name on your Airmax account (first and last name).');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/antenna-status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountName: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Could not check antenna status');
      }
      setMatchedName(data.matchedName ? String(data.matchedName) : null);
      setMessage(String(data.message || ''));
      setOutage(Boolean(data.outage));
      const ant = data.antenna;
      setAntenna(
        ant
          ? {
              found: Boolean(ant.found),
              status: String(ant.status || 'unknown'),
              statusLabel: String(ant.statusLabel || ''),
              isOnline: typeof ant.isOnline === 'boolean' ? ant.isOnline : null,
              signal: ant.signal != null && ant.signal !== '' ? Number(ant.signal) : null,
              lastSeen: ant.lastSeen ? String(ant.lastSeen) : null,
              model: ant.model ? String(ant.model) : null,
            }
          : null
      );
      setChecked(true);
      window.AirmaxVisitor?.note('Checked antenna status for account name');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check antenna status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="antenna-status" className="contents">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        onClick={() => (open ? closePanel() : openPanel(false))}
        className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors md:flex-col md:items-center md:justify-center md:gap-3 md:px-4 md:py-5 md:text-center ${
          open
            ? 'border-sky-400 bg-sky-50'
            : 'border-gray-200 bg-gradient-to-r from-white to-sky-50 hover:border-airmax-blue md:hover:bg-sky-50'
        }`}
      >
        <span className="flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-xl bg-sky-600 text-white flex-shrink-0">
          <SignalIcon className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1 md:flex-none">
          <p className="font-semibold text-gray-900 text-sm md:text-base">Check Antenna</p>
          <p className="text-xs text-gray-600 truncate md:whitespace-normal md:mt-1">
            <span className="md:hidden">See if your outdoor antenna is online right now</span>
            <span className="hidden md:inline">Is your antenna online?</span>
          </p>
        </div>
        {open ? (
          <ChevronUpIcon className="h-5 w-5 text-sky-700 flex-shrink-0 md:hidden" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-sky-700 flex-shrink-0 md:hidden" />
        )}
      </button>

      {open && (
        <div className="rounded-2xl border border-sky-200 bg-white p-4 md:p-5 shadow-sm space-y-3 md:col-span-3">
          <p className="text-sm text-gray-600">
            Enter the name on your Airmax account (as on your bill).
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <input
              ref={inputRef}
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void check();
              }}
              className="flex-1 rounded-xl border border-gray-300 px-3 py-3 text-base bg-white"
              autoComplete="name"
              placeholder="First and last name on your bill"
              aria-label="Name on your Airmax account"
              inputMode="text"
            />
            <button
              type="button"
              disabled={loading || accountName.trim().length < 2}
              onClick={() => void check()}
              className="rounded-xl bg-airmax-blue px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40 sm:min-w-[140px]"
            >
              {loading ? 'Checking…' : 'Check status'}
            </button>
          </div>

          {error && <p className="text-sm text-red-700">{error}</p>}

          {checked && (
            <div className="space-y-3 pt-1">
              {outage && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  There appears to be an active outage affecting this account. Please wait while we
                  restore service — usually 10–20 minutes.
                </div>
              )}
              <AntennaStatusCard antenna={antenna} matchedName={matchedName} />
              {!antenna?.found && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  {message ||
                    'We could not find an outdoor antenna linked to that account name. Double-check the name on your bill.'}
                </div>
              )}
              {antenna?.found && message && !outage ? (
                <p className="text-xs text-gray-500">{message}</p>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AfterHoursSupportPanel: React.FC<{ query?: string }> = ({ query }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<EmStatus | null>(null);
  const [statusError, setStatusError] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [step, setStep] = useState(0);
  const [triedSteps, setTriedSteps] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [otherDevicesOnline, setOtherDevicesOnline] = useState<boolean | null>(null);
  const [onlyOneDevice, setOnlyOneDevice] = useState<boolean | null>(null);
  const [antennaRouterOk, setAntennaRouterOk] = useState<boolean | null>(null);
  const [acceptFee, setAcceptFee] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [issue, setIssue] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [error, setError] = useState('');

  const feeApplies =
    otherDevicesOnline === true ||
    onlyOneDevice === true ||
    (antennaRouterOk === true && otherDevicesOnline !== false);

  const applyStatusPayload = (data: any) => {
    const ant = data.network?.antenna;
    setStatus({
      businessHours: Boolean(data.businessHours),
      feeMax: Number(data.feeMax) || 25,
      responseHours: Number(data.responseHours) || 2,
      phoneSupport: String(data.phoneSupport || '928-565-6707'),
      hoursLabel: String(data.hoursLabel || 'Monday–Friday, 9 AM – 5 PM (Arizona Time)'),
      network: {
        ok: Boolean(data.network?.ok),
        outage: Boolean(data.network?.outage),
        affectsAccount: Boolean(data.network?.affectsAccount),
        found: Boolean(data.network?.found),
        matchedName: data.network?.matchedName ? String(data.network.matchedName) : null,
        activeCount: Number(data.network?.activeCount) || 0,
        online: Number(data.network?.online) || 0,
        offline: Number(data.network?.offline) || 0,
        total: Number(data.network?.total) || 0,
        message: String(data.network?.message || ''),
        antenna: ant
          ? {
              found: Boolean(ant.found),
              status: String(ant.status || 'unknown'),
              statusLabel: String(ant.statusLabel || ''),
              isOnline: typeof ant.isOnline === 'boolean' ? ant.isOnline : null,
              signal: ant.signal != null && ant.signal !== '' ? Number(ant.signal) : null,
              lastSeen: ant.lastSeen ? String(ant.lastSeen) : null,
              model: ant.model ? String(ant.model) : null,
            }
          : null,
      },
    });
  };

  const loadStatus = async (forAccount?: string) => {
    setLoadingStatus(true);
    setStatusError('');
    try {
      const qs = forAccount?.trim()
        ? `?accountName=${encodeURIComponent(forAccount.trim())}`
        : '';
      const res = await fetch(`/api/emergency-support.php${qs}`, { cache: 'no-store' });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Could not load support options');
      }
      applyStatusPayload(data);
      return data as { network?: EmStatus['network'] };
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Could not load support options');
      return null;
    } finally {
      setLoadingStatus(false);
    }
  };

  const openPanel = () => {
    setOpen(true);
    setStep(0);
    setTriedSteps(false);
    setAccountName('');
    setOtherDevicesOnline(null);
    setOnlyOneDevice(null);
    setAntennaRouterOk(null);
    setAcceptFee(false);
    setResultMsg('');
    setError('');
    void loadStatus();
  };

  const continueAfterAccountCheck = async () => {
    setError('');
    if (!triedSteps) return;
    if (!accountName.trim()) {
      setError('Enter the name on your Airmax account (as on your bill).');
      return;
    }
    setCheckingAccount(true);
    try {
      const data = await loadStatus(accountName.trim());
      if (!data) return;
      if (data.network?.outage) {
        setStep(0);
        return;
      }
      if (!name.trim()) setName(accountName.trim());
      // Show antenna status step before device questions / SMS
      setStep(1);
    } finally {
      setCheckingAccount(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    setResultMsg('');
    try {
      const res = await fetch('/api/emergency-support.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || accountName,
          accountName: accountName.trim(),
          phone,
          issue,
          query: query || '',
          triedSteps,
          otherDevicesOnline,
          onlyOneDevice,
          antennaRouterOk,
          acceptFee: feeApplies ? acceptFee : false,
          website: honeypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Request failed');
      }
      setResultMsg(String(data.message || 'On-call support was notified.'));
      setStep(99);
      window.AirmaxVisitor?.note('Submitted after-hours emergency support SMS');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={openPanel}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 py-3.5 text-sm font-semibold text-amber-950 hover:bg-amber-100"
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5 text-amber-700" />
        Still stuck? Request after-hours help by text
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 md:p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-gray-900">After-hours emergency text</p>
          <p className="text-sm text-gray-600 mt-1">
            Only if you already tried the steps above and still need help.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs font-semibold text-gray-500 hover:text-gray-800"
        >
          Close
        </button>
      </div>

      {loadingStatus && !checkingAccount && (
        <p className="text-sm text-gray-600">Checking Airmax Cloud outages…</p>
      )}
      {statusError && <p className="text-sm text-red-700">{statusError}</p>}

      {status && (
        <>
          {status.businessHours ? (
            <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700">
              Phone support is open now ({status.hoursLabel}). Prefer calling{' '}
              <a href={`tel:${status.phoneSupport.replace(/\D/g, '')}`} className="font-semibold text-airmax-blue">
                {status.phoneSupport}
              </a>
              . Use after-hours text only if you cannot reach anyone.
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700">
              Outside phone hours ({status.hoursLabel}). On-call replies by text — usually within{' '}
              {status.responseHours} hours.
            </div>
          )}

          {status.network.outage ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 space-y-2">
              <p className="font-semibold">Outage on your account — do not request paid help yet</p>
              <p>{status.network.message}</p>
              {status.network.matchedName ? (
                <p className="text-xs text-red-800">Matched account: {status.network.matchedName}</p>
              ) : null}
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700">
              {status.network.message || 'Checking your connection against Airmax Cloud outages.'}
            </div>
          )}
        </>
      )}

      {status && !status.network.outage && step !== 99 && (
        <div className="space-y-4">
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">1) Confirm steps and your account</p>
              <label className="flex items-start gap-3 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={triedSteps}
                  onChange={(e) => setTriedSteps(e.target.checked)}
                />
                <span>Yes — I followed those steps and it is still not working.</span>
              </label>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  Name on your Airmax account (as on your bill)
                </label>
                <input
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base bg-white"
                  autoComplete="name"
                  placeholder="First and last name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  We use this to check your outdoor antenna and any Cloud outages for your connection.
                </p>
              </div>
              {error && <p className="text-sm text-red-700">{error}</p>}
              <button
                type="button"
                disabled={!triedSteps || !accountName.trim() || checkingAccount || loadingStatus}
                onClick={() => void continueAfterAccountCheck()}
                className="w-full rounded-xl bg-airmax-blue py-3 text-sm font-semibold text-white disabled:opacity-40"
              >
                {checkingAccount || loadingStatus ? 'Checking your antenna…' : 'Check antenna & continue'}
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-gray-900">2) Your outdoor antenna status</p>
              <AntennaStatusCard
                antenna={status.network.antenna}
                matchedName={status.network.matchedName}
              />
              {!status.network.antenna && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
                  We could not load your antenna status right now. You can still continue if this is an emergency.
                </div>
              )}
              <p className="text-sm text-gray-600">
                Review this before we text on-call support. If the antenna shows online but you still have no
                internet, the problem may be inside the house (router/Wi‑Fi/device).
              </p>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full rounded-xl bg-airmax-blue py-3 text-sm font-semibold text-white"
              >
                I see my antenna status — continue
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <AntennaStatusCard
                antenna={status.network.antenna}
                matchedName={status.network.matchedName}
              />
              <p className="text-sm font-semibold text-gray-900">3) Help us understand the problem</p>

              <div>
                <p className="text-sm text-gray-800 mb-2">
                  At your house right now, do your phone (Wi‑Fi) or computer have internet?
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOtherDevicesOnline(true)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      otherDevicesOnline === true ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    Yes, they work
                  </button>
                  <button
                    type="button"
                    onClick={() => setOtherDevicesOnline(false)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      otherDevicesOnline === false ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    No, nothing works
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-800 mb-2">Is only one device affected (for example only the TV)?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOnlyOneDevice(true)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      onlyOneDevice === true ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    Yes, only one
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnlyOneDevice(false)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      onlyOneDevice === false ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    No, more than one
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-800 mb-2">
                  Are the outdoor antenna and indoor router powered on (lights on)?
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAntennaRouterOk(true)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      antennaRouterOk === true ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setAntennaRouterOk(false)}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${
                      antennaRouterOk === false ? 'border-airmax-blue bg-blue-50 text-airmax-blue' : 'border-gray-200 bg-white'
                    }`}
                  >
                    No / not sure
                  </button>
                </div>
              </div>

              <button
                type="button"
                disabled={otherDevicesOnline === null || onlyOneDevice === null || antennaRouterOk === null}
                onClick={() => setStep(3)}
                className="w-full rounded-xl bg-airmax-blue py-3 text-sm font-semibold text-white disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <AntennaStatusCard
                antenna={status.network.antenna}
                matchedName={status.network.matchedName}
              />
              {feeApplies ? (
                <div className="rounded-xl border border-amber-300 bg-white px-4 py-3 text-sm text-gray-800 space-y-2">
                  <p className="font-semibold text-amber-950">Paid after-hours help</p>
                  <p>
                    Because internet works on other devices, or only one device is affected, this is not a
                    whole-home outage. After-hours support for device/setup help can be billed{' '}
                    <strong>up to ${status.feeMax} per incident</strong>. Reply may take up to{' '}
                    {status.responseHours} hours.
                  </p>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={acceptFee}
                      onChange={(e) => setAcceptFee(e.target.checked)}
                    />
                    <span>I understand and accept a possible charge up to ${status.feeMax} for this after-hours help.</span>
                  </label>
                </div>
              ) : (
                <div className="rounded-xl border border-green-200 bg-white px-4 py-3 text-sm text-gray-800">
                  This looks like a whole-home service emergency (no other devices online). We will text
                  on-call support. Reply may take up to {status.responseHours} hours.
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Your name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Mobile number (for text reply)</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="928-555-1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">What still is not working?</label>
                  <textarea
                    value={issue}
                    onChange={(e) => setIssue(e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base"
                    placeholder="Short description after you tried the steps…"
                  />
                </div>
                <div className="hidden" aria-hidden="true">
                  <label>Website</label>
                  <input value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} />
                </div>
              </div>

              {error && <p className="text-sm text-red-700">{error}</p>}

              <button
                type="button"
                disabled={
                  submitting ||
                  !name.trim() ||
                  !phone.trim() ||
                  !issue.trim() ||
                  (feeApplies && !acceptFee)
                }
                onClick={() => void submit()}
                className="w-full rounded-xl bg-amber-700 py-3.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-amber-800"
              >
                {submitting ? 'Sending…' : 'Text on-call support now'}
              </button>
            </div>
          )}
        </div>
      )}

      {step === 99 && resultMsg && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          {resultMsg}
        </div>
      )}
    </div>
  );
};

const AssistantChatCard: React.FC<{
  reply: AssistantReply;
  query?: string;
  onSuggestion: (s: string) => void;
  onOpenGuide?: () => void;
  onClear?: () => void;
}> = ({ reply, query, onSuggestion, onOpenGuide, onClear }) => (
  <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <ChatBubbleLeftRightIcon className="h-5 w-5 text-airmax-blue flex-shrink-0" />
        <p className="text-sm font-semibold text-airmax-blue truncate">Airmax Support Assistant</p>
      </div>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex-shrink-0"
        >
          Clear
        </button>
      )}
    </div>
    <div className="p-5 md:p-6 space-y-5">
      {query?.trim() && (
        <div className="flex justify-end">
          <div className="max-w-[90%] rounded-2xl rounded-br-md bg-airmax-blue text-white px-4 py-2.5 text-base leading-snug">
            {query.trim()}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-5 md:px-5 space-y-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-snug">{reply.title}</h2>
          {reply.intro && (
            <p className="mt-2 text-sm text-gray-500">{reply.intro}</p>
          )}
        </div>

        {reply.summary && (
          <p className="text-base md:text-lg text-gray-700 leading-relaxed">
            {reply.summary}
          </p>
        )}

        {reply.steps && reply.steps.length > 0 && (
          <ol className="space-y-3">
            {reply.steps.map((step, i) => (
              <li
                key={`${i}-${step.slice(0, 24)}`}
                className="flex gap-3 rounded-xl bg-gray-50 border border-gray-100 px-3.5 py-3"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-airmax-blue text-sm font-bold text-white">
                  {i + 1}
                </span>
                <span className="pt-1 text-base text-gray-800 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        )}

        {reply.message && !reply.summary && !reply.steps?.length && (
          <p className="text-base md:text-lg text-gray-700 leading-relaxed whitespace-pre-line">
            {reply.message}
          </p>
        )}
      </div>

      {(reply.actionUrl || (reply.elementId && onOpenGuide)) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {reply.actionUrl ? (
            <a
              href={reply.actionUrl}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-airmax-blue px-5 py-3.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {reply.actionLabel || 'Open'}
              <ArrowRightIcon className="h-4 w-4" />
            </a>
          ) : onOpenGuide && reply.elementId ? (
            <button
              type="button"
              onClick={onOpenGuide}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-airmax-blue px-5 py-3.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {reply.actionLabel || 'Show me on this page'}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      )}

      {reply.suggestions && reply.suggestions.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Or ask about:</p>
          <div className="flex flex-wrap gap-2">
            {reply.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSuggestion(s)}
                className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-airmax-blue hover:bg-blue-100"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <AfterHoursSupportPanel query={query} />
    </div>
  </div>
);

const Support: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    issue: '',
    description: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [correctedQuery, setCorrectedQuery] = useState('');
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);

  // Hide sales chat widget on Support — clients use Ask Me Anything search instead
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-airmax-chat-support';
    style.textContent = `
      [id*="airmax-chat"], [class*="airmax-chat"],
      [id*="airmax-widget"], [class*="airmax-widget"],
      [id*="airmax-greeting"] {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    const hideChat = () => {
      const w = window;
      if (!w.AirmaxChat) return;
      if (w.AirmaxChat.isOpen) {
        w.AirmaxChat.isOpen = false;
        const win = document.getElementById('airmax-chat-window');
        if (win) win.style.display = 'none';
      }
      w.AirmaxChat.greetingBubbleShown = false;
      w.AirmaxChat.hideGreetingBubble?.();
      w.AirmaxChat.hide?.();
    };
    hideChat();
    const t = window.setInterval(hideChat, 500);
    const stop = window.setTimeout(() => window.clearInterval(t), 8000);

    return () => {
      window.clearInterval(t);
      window.clearTimeout(stop);
      document.getElementById('hide-airmax-chat-support')?.remove();
    };
  }, []);

  // Admin FAQ panel state
  const [adminClickCount, setAdminClickCount] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isFaqAdmin, setIsFaqAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [faqs, setFaqs] = useState<FaqItem[]>(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('airmax_faqs') || 'null');
      if (Array.isArray(cached) && cached.length > 0) {
        return cached.map((f: FaqItem) => ({
          ...f,
          answer: scrubOffSiteHelp(String(f.answer || '')),
        }));
      }
    } catch { /* ignore */ }
    return DEFAULT_FAQS;
  });
  const [faqSaveStatus, setFaqSaveStatus] = useState('');
  const [faqSaving, setFaqSaving] = useState(false);
  const [newFaqQ, setNewFaqQ] = useState('');
  const [newFaqA, setNewFaqA] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Client FAQ suggestion form
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [suggestQuestion, setSuggestQuestion] = useState('');
  const [suggestName, setSuggestName] = useState('');
  const [suggestEmail, setSuggestEmail] = useState('');
  const [suggestHoneypot, setSuggestHoneypot] = useState('');
  const [captchaPrompt, setCaptchaPrompt] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [suggestSuccess, setSuggestSuccess] = useState('');
  const [pendingSuggestions, setPendingSuggestions] = useState<
    Array<{ id: string; question: string; name?: string; email?: string; createdAt?: string }>
  >([]);
  const [approveAnswer, setApproveAnswer] = useState<Record<string, string>>({});
  const [suggestAdminBusy, setSuggestAdminBusy] = useState(false);

  // Support Center improvement feedback
  const [showCenterFeedbackForm, setShowCenterFeedbackForm] = useState(false);
  const [centerLiked, setCenterLiked] = useState('');
  const [centerDisliked, setCenterDisliked] = useState('');
  const [centerIdeas, setCenterIdeas] = useState('');
  const [centerName, setCenterName] = useState('');
  const [centerEmail, setCenterEmail] = useState('');
  const [centerHoneypot, setCenterHoneypot] = useState('');
  const [centerCaptchaPrompt, setCenterCaptchaPrompt] = useState('');
  const [centerCaptchaToken, setCenterCaptchaToken] = useState('');
  const [centerCaptchaAnswer, setCenterCaptchaAnswer] = useState('');
  const [centerLoading, setCenterLoading] = useState(false);
  const [centerError, setCenterError] = useState('');
  const [centerSuccess, setCenterSuccess] = useState('');
  const [pendingCenterFeedback, setPendingCenterFeedback] = useState<
    Array<{
      id: string;
      liked?: string;
      disliked?: string;
      ideas?: string;
      name?: string;
      email?: string;
      createdAt?: string;
    }>
  >([]);
  const [searchResults, setSearchResults] = useState<Array<{
    type: 'faq' | 'troubleshooting' | 'section' | 'page';
    title: string;
    content: string;
    elementId?: string;
    url?: string;
    score: number;
  }>>([]);
  /** Chat-style reply — one spoken answer, not a results list */
  const [assistantReply, setAssistantReply] = useState<AssistantReply | null>(null);
  
  const faqSectionRef = useRef<HTMLDivElement>(null);
  const troubleshootingSectionRef = useRef<HTMLDivElement>(null);
  const interactiveSectionRef = useRef<HTMLDivElement>(null);
  const supportAudioRef = useRef<HTMLAudioElement | null>(null);
  const searchTimerRef = useRef<number | null>(null);
  const [isSupportAudioPlaying, setIsSupportAudioPlaying] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  /** Hide sticky Ask Me Anything on mobile while Interactive Troubleshooting fills the screen */
  const [hideMobileSearchForTroubleshoot, setHideMobileSearchForTroubleshoot] = useState(false);

  const persistFaqsLocal = (updated: FaqItem[]) => {
    setFaqs(updated);
    try {
      localStorage.setItem('airmax_faqs', JSON.stringify(updated));
    } catch { /* ignore */ }
  };

  const saveFaqsToServer = async (updated: FaqItem[]) => {
    setFaqSaving(true);
    setFaqSaveStatus('Saving…');
    try {
      const res = await fetch('/api/faqs.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: ADMIN_PASSWORD, faqs: updated }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Save failed');
      }
      persistFaqsLocal(data.faqs || updated);
      setFaqSaveStatus(`Saved ${ (data.faqs || updated).length } FAQs — live for everyone`);
    } catch (err) {
      persistFaqsLocal(updated);
      setFaqSaveStatus(
        err instanceof Error
          ? `Saved on this device only (${err.message}). Server save failed.`
          : 'Saved on this device only. Server save failed.'
      );
    } finally {
      setFaqSaving(false);
      window.setTimeout(() => setFaqSaveStatus(''), 4000);
    }
  };

  // Load FAQs from server (shared for all visitors)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/faqs.php', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.success && Array.isArray(data.faqs) && data.faqs.length > 0) {
          const slowDefault = DEFAULT_FAQS.find((f) => /slow/i.test(f.question));
          const cleaned = data.faqs.map((f: FaqItem) => ({
            ...f,
            answer: scrubOffSiteHelp(String(f.answer || '')),
          }));
          if (slowDefault) {
            const idx = cleaned.findIndex((f: FaqItem) => /slow/i.test(f.question));
            if (idx >= 0) cleaned[idx] = { ...cleaned[idx], answer: slowDefault.answer };
          }
          persistFaqsLocal(cleaned);
        }
      } catch {
        // keep defaults / local cache
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAdminTitleClick = () => {
    const next = adminClickCount + 1;
    setAdminClickCount(next);
    if (next >= 3) {
      setAdminClickCount(0);
      setShowPasswordModal(true);
      setAdminPassword('');
      setAdminError('');
    }
  };

  const handleAdminLogin = () => {
    if (adminPassword === ADMIN_PASSWORD) {
      setShowPasswordModal(false);
      setShowAdminPanel(true);
      setIsFaqAdmin(true);
      setAdminError('');
      void loadPendingSuggestions();
      void loadPendingCenterFeedback();
    } else {
      setAdminError('Incorrect password. Try again.');
    }
  };

  const loadCaptcha = async () => {
    try {
      const res = await fetch('/api/faq-suggest.php', { credentials: 'same-origin', cache: 'no-store' });
      const data = await res.json();
      if (!res.ok || !data?.success || !data.captcha) {
        throw new Error(data?.message || 'Could not load security check');
      }
      setCaptchaPrompt(data.captcha.prompt || `What is ${data.captcha.a} + ${data.captcha.b}?`);
      setCaptchaToken(data.captcha.token || '');
      setCaptchaAnswer('');
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : 'Could not load security check');
    }
  };

  const loadCenterCaptcha = async () => {
    try {
      const res = await fetch('/api/faq-suggest.php', { credentials: 'same-origin', cache: 'no-store' });
      const data = await res.json();
      if (!res.ok || !data?.success || !data.captcha) {
        throw new Error(data?.message || 'Could not load security check');
      }
      setCenterCaptchaPrompt(data.captcha.prompt || `What is ${data.captcha.a} + ${data.captcha.b}?`);
      setCenterCaptchaToken(data.captcha.token || '');
      setCenterCaptchaAnswer('');
    } catch (err) {
      setCenterError(err instanceof Error ? err.message : 'Could not load security check');
    }
  };

  const openSuggestForm = async () => {
    setShowSuggestForm(true);
    setShowCenterFeedbackForm(false);
    setSuggestError('');
    setSuggestSuccess('');
    await loadCaptcha();
  };

  const openCenterFeedbackForm = async () => {
    setShowCenterFeedbackForm(true);
    setShowSuggestForm(false);
    setCenterError('');
    setCenterSuccess('');
    await loadCenterCaptcha();
  };

  const submitFaqSuggestion = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuggestError('');
    setSuggestSuccess('');
    setSuggestLoading(true);
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          action: 'suggest',
          question: suggestQuestion,
          name: suggestName,
          email: suggestEmail,
          captchaToken,
          captchaAnswer,
          website: suggestHoneypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (data.captchaRequired) {
          await loadCaptcha();
        }
        throw new Error(data.message || 'Could not submit your question');
      }
      setSuggestSuccess(data.message || 'Thanks! We received your question.');
      setSuggestQuestion('');
      window.AirmaxVisitor?.note('Submitted FAQ suggestion');
      setSuggestName('');
      setSuggestEmail('');
      setSuggestHoneypot('');
      setCaptchaAnswer('');
      await loadCaptcha();
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : 'Could not submit your question');
    } finally {
      setSuggestLoading(false);
    }
  };

  const submitCenterFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setCenterError('');
    setCenterSuccess('');
    setCenterLoading(true);
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          action: 'center_feedback',
          liked: centerLiked,
          disliked: centerDisliked,
          ideas: centerIdeas,
          name: centerName,
          email: centerEmail,
          captchaToken: centerCaptchaToken,
          captchaAnswer: centerCaptchaAnswer,
          website: centerHoneypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (data.captchaRequired) {
          await loadCenterCaptcha();
        }
        throw new Error(data.message || 'Could not submit your feedback');
      }
      setCenterSuccess(data.message || 'Thanks! Your feedback helps us improve.');
      setCenterLiked('');
      setCenterDisliked('');
      setCenterIdeas('');
      setCenterName('');
      setCenterEmail('');
      setCenterHoneypot('');
      setCenterCaptchaAnswer('');
      window.AirmaxVisitor?.note('Submitted Support Center feedback');
      await loadCenterCaptcha();
    } catch (err) {
      setCenterError(err instanceof Error ? err.message : 'Could not submit your feedback');
    } finally {
      setCenterLoading(false);
    }
  };

  const loadPendingSuggestions = async () => {
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'list', password: ADMIN_PASSWORD }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;
      setPendingSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch {
      /* ignore */
    }
  };

  const loadPendingCenterFeedback = async () => {
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'list_feedback', password: ADMIN_PASSWORD }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;
      setPendingCenterFeedback(Array.isArray(data.feedback) ? data.feedback : []);
    } catch {
      /* ignore */
    }
  };

  const dismissSuggestion = async (id: string) => {
    setSuggestAdminBusy(true);
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'dismiss', password: ADMIN_PASSWORD, id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Could not dismiss');
      }
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setFaqSaveStatus(err instanceof Error ? err.message : 'Could not dismiss suggestion');
    } finally {
      setSuggestAdminBusy(false);
    }
  };

  const dismissCenterFeedback = async (id: string) => {
    setSuggestAdminBusy(true);
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'dismiss_feedback', password: ADMIN_PASSWORD, id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Could not dismiss');
      }
      setPendingCenterFeedback((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setFaqSaveStatus(err instanceof Error ? err.message : 'Could not dismiss feedback');
    } finally {
      setSuggestAdminBusy(false);
    }
  };

  const approveSuggestion = async (id: string) => {
    const answer = (approveAnswer[id] || '').trim();
    if (!answer) {
      setFaqSaveStatus('Enter an answer before publishing this FAQ.');
      return;
    }
    setSuggestAdminBusy(true);
    try {
      const res = await fetch('/api/faq-suggest.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          action: 'approve',
          password: ADMIN_PASSWORD,
          id,
          answer,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Could not publish FAQ');
      }
      if (Array.isArray(data.faqs)) {
        persistFaqsLocal(data.faqs);
      }
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
      setApproveAnswer((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setFaqSaveStatus('Customer FAQ published for everyone');
      window.setTimeout(() => setFaqSaveStatus(''), 4000);
    } catch (err) {
      setFaqSaveStatus(err instanceof Error ? err.message : 'Could not publish FAQ');
    } finally {
      setSuggestAdminBusy(false);
    }
  };

  const handleAddFaq = () => {
    if (!newFaqQ.trim() || !newFaqA.trim()) return;
    let updated: FaqItem[];
    if (editingIndex !== null) {
      updated = [...faqs];
      updated[editingIndex] = { question: newFaqQ.trim(), answer: newFaqA.trim() };
      setEditingIndex(null);
    } else {
      updated = [...faqs, { question: newFaqQ.trim(), answer: newFaqA.trim() }];
    }
    setNewFaqQ('');
    setNewFaqA('');
    void saveFaqsToServer(updated);
  };

  const handleDeleteFaq = (index: number) => {
    if (!window.confirm('Delete this FAQ?')) return;
    const updated = faqs.filter((_, i) => i !== index);
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewFaqQ('');
      setNewFaqA('');
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
    void saveFaqsToServer(updated);
  };

  const handleEditFaq = (index: number) => {
    setNewFaqQ(faqs[index].question);
    setNewFaqA(faqs[index].answer);
    setEditingIndex(index);
    setShowAdminPanel(true);
  };

  const handleMoveFaq = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= faqs.length) return;
    const updated = [...faqs];
    const tmp = updated[index];
    updated[index] = updated[target];
    updated[target] = tmp;
    if (editingIndex === index) setEditingIndex(target);
    else if (editingIndex === target) setEditingIndex(index);
    void saveFaqsToServer(updated);
  };

  const troubleshootingSteps = [
    {
      title: 'No Internet Connection',
      icon: ExclamationTriangleIcon,
      steps: [
        'Check if the issue affects all devices',
        'Restart your router and modem',
        'Check all cable connections',
        'Verify account status in customer portal',
        'Contact support if issue persists'
      ]
    },
    {
      title: 'Slow Speeds',
      icon: WrenchScrewdriverIcon,
      steps: [
        'Restart your router (unplug 30 seconds, then plug back in)',
        'Check for background downloads/uploads on other devices',
        'Limit number of connected devices',
        'Move closer to router if using Wi-Fi',
        'Follow Interactive Connection Troubleshooting on this page',
        'Contact support if issue persists'
      ]
    },
    {
      title: 'Wi-Fi Connection Issues',
      icon: ChatBubbleLeftRightIcon,
      steps: [
        'Restart your device\'s Wi-Fi',
        'Forget and reconnect to the network',
        'Check if you\'re within range',
        'Restart your router',
        'Update your device\'s network drivers'
      ]
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit to the backend
    console.log('Support form submitted:', supportForm);
    alert('Support ticket submitted successfully! We\'ll respond within 24 hours.');
    setSupportForm({
      name: '',
      email: '',
      subject: '',
      issue: '',
      description: ''
    });
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Levenshtein distance for fuzzy matching
  const levenshtein = (a: string, b: string): number => {
    const m = a.length, n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
      Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        dp[i][j] = a[i-1] === b[j-1]
          ? dp[i-1][j-1]
          : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    return dp[m][n];
  };

  // Known dictionary of support keywords for autocorrect
  const dictionary = [
    'internet','connection','router','wifi','wireless','reboot','restart',
    'slow','speed','outage','signal','network','cable','modem','antenna',
    'billing','payment','account','plan','upgrade','install','setup',
    'tv','streaming','netflix','phone','support','troubleshoot','password',
    'login','portal','service','coverage','tower','status','maintenance',
    'download','upload','ping','latency','bandwidth','data','device',
    'browser','computer','laptop','mobile','tablet','lights','working',
    'indicator','ethernet','wired','disconnected','dropping','intermittent',
    'broken','problem','issue','error','fix','help','connect','connected',
    'disconnected','reset','restarting','rebooting','blinking','flashing',
    'extend','extension','invoice','due','late','pay','bill','balance',
  ];

  // Map a troubleshooting step text to the best search query for it
  const stepToSearchQuery = (step: string): string => {
    const s = step.toLowerCase();
    if (s.includes('restart your router') || s.includes('reboot') || s.includes('restart router') || s.includes('unplug')) return 'how do i reboot my router';
    if (s.includes('interactive connection') || s.includes('interactive troubleshooting')) return 'no internet connection';
    if (s.includes('background download') || s.includes('background upload')) return 'slow internet too many devices';
    if (s.includes('connected devices') || s.includes('number of devices')) return 'slow internet too many devices';
    if (s.includes('closer to router') || s.includes('move closer') || s.includes('wi-fi')) return 'improve wifi signal';
    if (s.includes('cable') || s.includes('connections') || s.includes('check all')) return 'no internet connection cables';
    if (s.includes('restart your device') || (s.includes('device') && s.includes('wi-fi'))) return 'wifi not working restart device';
    if (s.includes('forget') && s.includes('reconnect')) return 'forget reconnect wifi network';
    if (s.includes('network drivers')) return 'update network drivers wifi';
    if (s.includes('account') || s.includes('portal')) return 'login customer portal account';
    if (s.includes('service status') || s.includes('outage')) return 'network status outage tower';
    if (s.includes('within range') || s.includes('range')) return 'wifi signal range weak';
    if (s.includes('contact') || s.includes('support')) return 'contact support phone';
    return step.replace(/[^a-z0-9 ]/gi, '').toLowerCase();
  };

  const autocorrectWord = (word: string): string => {
    if (word.length < 4) return word; // skip short words
    let best = word, bestDist = 2; // max allowed edit distance
    dictionary.forEach(known => {
      const dist = levenshtein(word, known);
      if (dist < bestDist) { best = known; bestDist = dist; }
    });
    return best;
  };

  /** High-confidence natural-language intents → guaranteed top results */
  const matchPriorityIntents = (rawQuery: string) => {
    const q = rawQuery.toLowerCase();
    const hits: Array<{
      type: 'faq' | 'troubleshooting' | 'section' | 'page';
      title: string;
      content: string;
      elementId?: string;
      url?: string;
      score: number;
    }> = [];

    const isTvTopic =
      /\b(tv|television|smart.?tv|roku|firestick|fire stick|apple tv|chromecast|netflix|hulu|disney|youtube|streaming app)\b/.test(q);
    const isVirusTopic =
      /\b(virus|malware|spyware|trojan|worm|infected|antivirus|anti.?virus|housecall|trend micro)\b/.test(q) ||
      /\b(scan (my )?(pc|computer|laptop|device)|virus scan|free (virus )?scanner)\b/.test(q);

    const isAntennaStatusTopic =
      /\b(antenna status|check (my )?antenna|is my antenna|outdoor antenna|cpe status|radio status)\b/.test(q) ||
      (/\bantenna\b/.test(q) && /\b(status|online|offline|check|down|working)\b/.test(q));

    const wantsBillExtension =
      (/\b(extend|extension|postpone|push back|pushback)\b/.test(q) &&
        /\b(bill|payment|invoice|due|pay)\b/.test(q)) ||
      /\b(more time to pay|need more time|cant pay|can't pay|cannot pay|late (on )?(my )?bill|due date|payment extension|extend (my )?(bill|payment|invoice)|bill extension)\b/.test(q) ||
      /\b(need to extend|want to extend|how (do i|to) extend)\b/.test(q);

    // --- Topic: Smart TV / streaming (must win over generic “not working”) ---
    if (isTvTopic) {
      hits.push({
        type: 'troubleshooting',
        title: 'Smart TV not working — try this',
        content:
          'Use the Smart TV guide on this Support page. Start here:\n\n' +
          '1) Power cycle the TV — turn off, unplug from the wall, wait 60 seconds, plug back in, turn on\n' +
          '2) Forget and reconnect to your WiFi network in the TV settings\n' +
          '3) Clear cache/data for streaming apps (Netflix, YouTube, etc.)\n' +
          '4) Reset the TV network settings if it still will not connect\n' +
          '5) If the whole house has no internet, use Interactive Connection Troubleshooting for the router\n' +
          '6) Call 928-565-6707 if it is still broken (Mon–Fri, 9 AM – 5 PM Arizona Time)',
        elementId: 'tv-troubleshooting',
        url: '/support',
        score: 220,
      });
    }

    if (wantsBillExtension) {
      hits.push({
        type: 'page',
        title: 'Extend your bill due date',
        content:
          'Need more time to pay? Open Payment Extension, verify with your account phone number, choose an unpaid invoice, and extend the due date online. Eligible invoices can usually be extended one time.\n\n1) Open Payment Extension\n2) Verify with your account phone number\n3) Choose an unpaid invoice\n4) Pick extra days or a new due date',
        url: '/payment-extension',
        score: 200,
      });
    }

    if (
      /\b(pay (my )?bill|make a payment|pay invoice|billing portal|pay online)\b/.test(q) &&
      !wantsBillExtension
    ) {
      hits.push({
        type: 'page',
        title: 'Pay your bill online',
        content:
          'Pay your Airmax bill online through Pay Bill on this site.\n\n1) Open Pay Bill\n2) Verify with your account phone number\n3) Open your secure one-time payment link\n4) Complete payment',
        url: '/pay-bill',
        score: 180,
      });
    }

    if (/\b(update|change|add)\b/.test(q) && /\b(card|payment method|credit card|debit)\b/.test(q)) {
      hits.push({
        type: 'faq',
        title: 'How do I update my payment information?',
        content:
          'Log into your customer portal at https://www.airmaxbilling.com/crm/login, go to Account Settings → Payment Methods, and update your card.',
        url: 'https://www.airmaxbilling.com/crm/login',
        score: 180,
      });
    }

    if (
      /\b(reset|forgot|forgotten|change)\b/.test(q) &&
      /\b(password|passcode|login)\b/.test(q) &&
      !/\b(wifi|wi-fi|router|modem|ssid)\b/.test(q)
    ) {
      hits.push({
        type: 'page',
        title: 'Reset your billing portal password',
        content:
          'This resets your Airmax billing account password only — not your WiFi or router password.\n\n1) Open Reset Billing Password\n2) Enter your username (usually your email)\n3) Follow the email instructions to set a new password\n4) Log in at the billing portal',
        url: 'https://www.airmaxbilling.com/crm/reset-password',
        score: 200,
      });
    }

    if (isVirusTopic) {
      hits.push({
        type: 'page',
        title: 'Free Virus Scanner (Trend Micro HouseCall)',
        content:
          'You can scan your Windows computer for free with Trend Micro HouseCall. It checks for viruses, spyware, and other malware.\n\n1) Open the Free Virus Scanner link on this Support page (or use the button below)\n2) Choose Windows 64-bit or 32-bit\n3) Download the small HouseCall launcher and run a Quick Scan\n4) Review results and clean anything it finds\n\nHouseCall is a free on-demand scan tool from Trend Micro — it does not replace always-on antivirus.',
        url: 'https://www.trendmicro.com/en_ae/forHome/products/housecall.html',
        score: 200,
      });
    }

    if (isAntennaStatusTopic) {
      hits.push({
        type: 'section',
        title: 'Check your outdoor antenna status',
        content:
          'Enter the name on your Airmax account to see if your outdoor antenna is online right now — including signal strength when available.\n\n1) Tap Check Antenna Status on this Support page\n2) Type the first and last name on your bill\n3) Tap Check status\n4) Review online/offline, signal, and last seen',
        elementId: 'antenna-status',
        url: '/support',
        score: 210,
      });
    }

    // Router / internet down — never steal TV or billing questions
    const routerOrNetDown =
      !isTvTopic &&
      !wantsBillExtension &&
      !isVirusTopic &&
      !isAntennaStatusTopic &&
      ((/\b(router|modem|wifi|wi-fi|internet|connection)\b/.test(q) &&
        /\b(not working|isnt working|isn't working|wont work|won't work|broken|dead|down|offline)\b/.test(q)) ||
        /\b(no internet|no connection|no wifi|no wi-fi|cant connect|can't connect|cannot connect)\b/.test(q));

    if (routerOrNetDown) {
      hits.push({
        type: 'troubleshooting',
        title: 'What should I do if I have no internet connection?',
        content:
          `Check whether other devices are affected, restart the router, and verify cables. Use the Interactive Connection Troubleshooting guide on this page for pictures.\n\n` +
          `1) Unplug your router for 30 seconds, plug it back in, and wait 2–3 minutes\n` +
          `2) Check if the issue affects all devices\n` +
          `3) Check all cable connections\n` +
          `4) Open Interactive Connection Troubleshooting on this page (step-by-step with pictures)\n` +
          `5) Call 928-565-6707 if it is still down (Mon–Fri, 9 AM – 5 PM Arizona Time)`,
        elementId: 'interactive',
        url: '/support',
        score: 200,
      });
    }

    const internetSlow =
      !isTvTopic &&
      ((/\b(slow|sluggish|buffering|laggy|lagging|crawling)\b/.test(q) &&
        /\b(internet|wifi|wi-fi|connection|speed|speeds|download|upload)\b/.test(q)) ||
        /\b(internet is slow|slow internet|wifi is slow|wi-fi is slow|speeds? (are |is )?slow|why .{0,20}slow)\b/.test(q));

    if (internetSlow) {
      const slowGuide = troubleshootingSteps.find((g) => /slow/i.test(g.title));
      const steps = (slowGuide?.steps || [
        'Restart your router (unplug 30 seconds, then plug back in)',
        'Check for background downloads/uploads on other devices',
        'Limit number of connected devices',
        'Move closer to router if using Wi-Fi',
        'Follow Interactive Connection Troubleshooting on this page',
        'Contact support if issue persists',
      ]).map((s, i) => `${i + 1}) ${s}`).join('\n');
      hits.push({
        type: 'faq',
        title: 'Why is my internet slow?',
        content:
          `Slow speeds are often caused by distance from the router, too many devices online, network congestion, or weather on a wireless link.\n\n${steps}`,
        elementId: 'troubleshooting',
        url: '/support',
        score: 200,
      });
    }

    // Reboot router specifically
    if (!isTvTopic && /\b(reboot|restart|reset|power cycle|powercycle)\b/.test(q) && /\b(router|modem)\b/.test(q)) {
      hits.push({
        type: 'faq',
        title: 'How do I reboot my router?',
        content:
          '1) Unplug your router from power\n2) Wait 30 seconds\n3) Plug it back in\n4) Wait 2–3 minutes for it to fully restart\n5) If issues persist, open Interactive Connection Troubleshooting on this page or call 928-565-6707',
        elementId: 'interactive',
        url: '/support',
        score: 210,
      });
    }

    // Outage / tower status
    if (/\b(outage|tower|network status|service (is )?down|is (there|airmax) down)\b/.test(q)) {
      hits.push({
        type: 'page',
        title: 'Network Status',
        content:
          'Check live tower and network status on our Network Status page.\n\n1) Open Network Status\n2) Look for your area / tower\n3) If everything looks up but you still have no service, restart the router and use Interactive Connection Troubleshooting',
        url: '/tower-status',
        score: 190,
      });
    }

    return hits;
  };

  const runAssistantLookup = (query: string) => {
    setCorrectedQuery('');
    if (!query.trim()) {
      setSearchResults([]);
      setAssistantReply(null);
      setIsLookingUp(false);
      return;
    }

    // Chat-style greetings / help (for people who treat this like a chat)
    const chatReply = getChatStyleReply(query);
    if (chatReply) {
      setSearchResults([]);
      setAssistantReply(chatReply);
      setIsLookingUp(false);
      return;
    }

    // Stop words to ignore
    const stopWords = new Set(['my','i','is','are','not','the','a','an','it','its','do','does','to','how','why','what','when','where','can','cant','could','would','should','have','has','had','be','been','was','were','and','or','but','for','with','this','that','in','on','at','so','get','just','need','want','help','no','yes','please','thank','thanks','about','up','down','out','of','from']);

    // --- Step 1: Autocorrect the raw query words first ---
    const rawWords = query.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ').filter(w => w.length > 2 && !stopWords.has(w));
    const correctedWords = rawWords.map(w => autocorrectWord(w));
    const corrected = correctedWords.join(' ');
    const original = rawWords.join(' ');
    if (corrected !== original) setCorrectedQuery(corrected);
    // Build a corrected version of the full query for intent matching
    const correctedQueryLower = correctedWords.join(' ');

    // Intent expansions — NEVER let bare “not working” mean “no internet” for every device
    const intentMap: Record<string, string[]> = {
      'internet down': ['no internet', 'connection', 'outage', 'router'],
      'no internet': ['no internet', 'connection', 'outage', 'router'],
      'internet slow': ['slow', 'speed', 'router'],
      'slow internet': ['slow', 'speed', 'router'],
      'cant connect': ['connection', 'wifi', 'router'],
      'no connection': ['connection', 'no internet', 'outage', 'router'],
      'wifi not working': ['wifi', 'wireless', 'router', 'signal'],
      'reboot router': ['reboot', 'router', 'restart'],
      'reset router': ['reboot', 'router', 'restart'],
      'restart router': ['reboot', 'router', 'restart'],
      'extend bill': ['extend', 'extension', 'due date', 'payment extension', 'invoice', 'billing'],
      'extend payment': ['extend', 'extension', 'due date', 'payment extension', 'invoice'],
      'due date': ['extend', 'extension', 'due date', 'payment extension', 'invoice', 'billing'],
      'more time': ['extend', 'extension', 'due date', 'payment extension', 'billing'],
      'cant pay': ['extend', 'extension', 'due date', 'payment extension', 'billing'],
      'late bill': ['extend', 'extension', 'due date', 'payment', 'billing', 'invoice'],
      'upgrade plan': ['plan', 'upgrade', 'speed'],
      'change plan': ['plan', 'upgrade', 'change'],
      'tv not working': ['tv', 'smart tv', 'streaming', 'wifi'],
      'smart tv': ['tv', 'smart tv', 'streaming'],
      'streaming': ['tv', 'streaming', 'smart tv'],
      'netflix': ['tv', 'streaming', 'smart tv', 'netflix'],
      'weak signal': ['signal', 'wifi', 'router'],
      'poor signal': ['signal', 'wifi', 'router'],
      'router lights': ['lights', 'router', 'indicator'],
      'outage': ['outage', 'tower', 'network status'],
      'virus': ['virus', 'malware', 'scanner', 'housecall'],
    };

    // --- Step 2: Run intent map on CORRECTED query ---
    let enrichedKeywords: string[] = [];
    const queryLower = query.toLowerCase();
    const topicIsTv =
      /\b(tv|television|smart.?tv|roku|firestick|netflix|hulu|disney|youtube|streaming)\b/.test(queryLower) ||
      /\b(tv|television|smart.?tv|roku|firestick|netflix|streaming)\b/.test(correctedQueryLower);

    Object.entries(intentMap).forEach(([phrase, expansions]) => {
      if (correctedQueryLower.includes(phrase) || queryLower.includes(phrase)) {
        // Don't let billing/internet expansions pollute a TV question
        if (topicIsTv && !/tv|smart|stream|netflix/.test(phrase) && /internet|bill|payment|outage|slow/.test(phrase)) {
          return;
        }
        enrichedKeywords.push(...expansions);
      }
    });

    // Topic-aware “not working” — expand only for the device they named
    if (/\b(not working|isnt working|isn't working|wont work|won't work|broken)\b/.test(queryLower)) {
      if (topicIsTv) {
        enrichedKeywords.push('tv', 'smart tv', 'streaming', 'wifi');
      } else if (/\b(router|modem)\b/.test(queryLower)) {
        enrichedKeywords.push('router', 'reboot', 'no internet', 'connection');
      } else if (/\b(wifi|wi-fi)\b/.test(queryLower)) {
        enrichedKeywords.push('wifi', 'wireless', 'signal', 'router');
      } else if (/\b(internet|connection)\b/.test(queryLower)) {
        enrichedKeywords.push('no internet', 'connection', 'router', 'outage');
      }
      // bare “X is not working” with unknown X → rely on the subject words only
    }

    const queryWords = correctedWords;

    const allKeywords = Array.from(new Set([...queryWords, ...enrichedKeywords]));

    // If no keywords found, fall back to full phrase
    if (allKeywords.length === 0) allKeywords.push(queryLower);

    const results: Array<{
      type: 'faq' | 'troubleshooting' | 'section' | 'page';
      title: string;
      content: string;
      elementId?: string;
      url?: string;
      score: number;
    }> = [];
    const searchLower = query.toLowerCase();

    // Priority natural-language intents (e.g. “I need to extend my bill”)
    matchPriorityIntents(query).forEach((hit) => results.push(hit));

    // Comprehensive knowledge base from all website pages
    const knowledgeBase: Array<{
      title: string;
      content: string;
      keywords?: string[];
      elementId?: string;
      type: 'faq' | 'troubleshooting' | 'section' | 'page';
      url: string;
    }> = [
      // Support page content (current page)
      ...faqs.map((faq, index) => ({
        title: faq.question,
        content: faq.answer,
        elementId: `faq-${index}`,
        type: 'faq' as const,
        url: '/support'
      })),
      ...troubleshootingSteps.flatMap((guide) => [
        {
          title: guide.title,
          content: guide.steps.join(' '),
          elementId: 'troubleshooting',
          type: 'troubleshooting' as const,
          url: '/support',
          keywords: guide.title.toLowerCase().includes('slow')
            ? ['slow', 'speed', 'internet']
            : guide.title.toLowerCase().includes('wifi')
              ? ['wifi', 'wireless', 'signal']
              : ['no internet', 'connection', 'router'],
        },
        ...guide.steps.map((step) => ({
          title: guide.title,
          content: step,
          elementId: 'troubleshooting',
          type: 'troubleshooting' as const,
          url: '/support',
        }))
      ]),

      // Smart TV guide (on this Support page)
      {
        title: 'Smart TV Troubleshooting',
        content:
          'Power cycle your Smart TV, forget and reconnect WiFi, clear app cache, and reset network settings. Full visual steps are on this Support page under Smart TV Troubleshooting.',
        keywords: [
          'tv', 'smart tv', 'television', 'streaming', 'netflix', 'roku', 'firestick',
          'youtube', 'wifi tv', 'tv not working', 'tv wont connect',
        ],
        elementId: 'tv-troubleshooting',
        type: 'troubleshooting' as const,
        url: '/support',
      },
      {
        title: 'Power Cycle Your Smart TV',
        content:
          'Turn off the TV, unplug from the wall, wait 60 seconds, plug back in, and turn on. This clears TV memory glitches.',
        keywords: ['tv', 'smart tv', 'power cycle', 'reboot tv', 'restart tv'],
        elementId: 'tv-troubleshooting',
        type: 'troubleshooting' as const,
        url: '/support',
      },

      // Interactive router guide
      {
        title: 'Interactive Connection Troubleshooting',
        content:
          'Step-by-step visual guide with pictures: check PoE injector, cables, router WAN port, and power-cycle the router.',
        keywords: [
          'router', 'poe', 'cable', 'no internet', 'connection', 'modem', 'interactive',
          'check router', 'how to check',
        ],
        elementId: 'interactive',
        type: 'troubleshooting' as const,
        url: '/support',
      },

      // Billing / payment extension (high-demand)
      {
        title: 'Extend your bill due date',
        content:
          'Need more time to pay your bill? Use Payment Extension to extend an unpaid invoice due date. Verify with your account phone number, choose the invoice, and pick extra days or a new due date. One-time extension per eligible invoice.',
        keywords: [
          'extend', 'extension', 'bill', 'billing', 'payment', 'invoice', 'due date',
          'more time', 'late', 'cant pay', "can't pay", 'postpone', 'pay later',
        ],
        type: 'page' as const,
        url: '/payment-extension',
      },
      {
        title: 'Pay your bill',
        content:
          'Pay your Airmax internet bill online through the billing portal. Update payment methods, view invoices, and manage your account balance.',
        keywords: ['pay bill', 'payment', 'billing', 'invoice', 'balance', 'pay online'],
        type: 'page' as const,
        url: '/pay-bill',
      },
      {
        title: 'Reset billing portal password',
        content:
          'Forgot your billing account password? Reset it for the Airmax billing portal only — this does not change your WiFi or router password. Use https://www.airmaxbilling.com/crm/reset-password and enter your username (usually your email).',
        keywords: [
          'reset password',
          'forgot password',
          'billing password',
          'portal password',
          'login password',
          'account password',
          'reset billing',
        ],
        type: 'page' as const,
        url: 'https://www.airmaxbilling.com/crm/reset-password',
      },
      {
        title: 'Billing and account help',
        content:
          'Questions about invoices, due dates, payment methods, plan changes, or extending a bill. Phone support Monday–Friday 9 AM–5 PM Arizona Time at 928-565-6707.',
        keywords: ['billing', 'account', 'invoice', 'payment', 'due date', 'extend'],
        type: 'section' as const,
        url: '/support',
      },
      
      // Home page content
      {
        title: 'High-Speed Internet Plans',
        content: 'Fast reliable internet service with speeds up to 1 Gbps. No data caps, no contracts. Professional installation available.',
        type: 'page' as const,
        url: '/'
      },
      {
        title: 'Coverage Areas',
        content: 'Serving rural communities with high-speed internet. Check if service is available in your area.',
        type: 'page' as const,
        url: '/'
      },
      {
        title: 'Contact Information',
        content: 'Call us at 928-565-6707 for sales and phone support Monday–Friday, 9 AM – 5 PM (Arizona Time). Online Support Center available 24/7 at airmaxcm.com/support.',
        type: 'page' as const,
        url: '/contact'
      },
      
      // Plans page content
      {
        title: 'Internet Plans and Pricing',
        content: 'Multiple speed tiers available: Basic 25 Mbps, Standard 100 Mbps, Fast 300 Mbps, Ultra 500 Mbps, Gigabit 1000 Mbps. All plans include unlimited data.',
        type: 'page' as const,
        url: '/plans'
      },
      {
        title: 'Installation Fees',
        content: 'Professional installation $199. Self-installation option available for $49. Equipment rental $10/month.',
        type: 'page' as const,
        url: '/plans'
      },
      
      // About page content
      {
        title: 'About Airmax Internet',
        content: 'Local internet service provider serving Arizona communities. Committed to bringing high-speed internet to rural areas. Family-owned business with over 10 years experience.',
        type: 'page' as const,
        url: '/about'
      },
      
      // Phone service content
      {
        title: 'VoIP Phone Service',
        content: 'Unlimited local and long distance calling. Caller ID, call waiting, voicemail included. $29.99/month when bundled with internet.',
        type: 'page' as const,
        url: '/phone-service'
      },
      
      // How it works content
      {
        title: 'How Our Service Works',
        content: 'Fixed wireless internet technology. Line-of-sight antenna installation. Professional mounting and alignment. Weather-resistant equipment.',
        type: 'page' as const,
        url: '/how-it-works'
      },
      {
        title: 'Installation Process',
        content: 'Site survey to confirm signal strength. Professional installation within 3-5 business days. Equipment setup and testing. Customer training included.',
        type: 'page' as const,
        url: '/how-it-works'
      },
      
      // Contact page content
      {
        title: 'Support Hours',
        content: 'Phone Support: Monday–Friday, 9 AM – 5 PM (Arizona Time). Saturday–Sunday: Online Self-Service Support Only. Online Support Center available 24/7 at https://airmaxcm.com/support. Live telephone assistance is available during regular business hours only.',
        type: 'page' as const,
        url: '/contact'
      },
      {
        title: 'Office Locations',
        content: 'Main office in Arizona. Service areas include rural communities. On-site support available.',
        type: 'page' as const,
        url: '/contact'
      },
      
      // Check service content
      {
        title: 'Service Availability Check',
        content: 'Check if high-speed internet is available at your address. Enter your address to see coverage options and available speeds.',
        type: 'page' as const,
        url: '/'
      },
      
      // Gamers page content
      {
        title: 'Gaming Internet Plans',
        content: 'Low-latency connections perfect for online gaming. Fast upload speeds for streaming. No data caps or throttling. Ping times under 20ms.',
        type: 'page' as const,
        url: '/gamers'
      },
      
      // Tower status content
      {
        title: 'Network Status',
        content: 'Real-time tower status and outage information. Maintenance schedules. Performance monitoring.',
        type: 'page' as const,
        url: '/tower-status'
      },

      // Free virus scan tool (Trend Micro HouseCall)
      {
        title: 'Free Virus Scanner (Trend Micro HouseCall)',
        content:
          'Scan your Windows computer for free with Trend Micro HouseCall. Detect and clean viruses, spyware, and other malware. Open the free scanner from this Support page under Quick help.',
        keywords: [
          'virus', 'malware', 'spyware', 'scan', 'scanner', 'antivirus', 'infected',
          'housecall', 'trend micro', 'trojan', 'worm', 'security scan',
        ],
        type: 'page' as const,
        url: 'https://www.trendmicro.com/en_ae/forHome/products/housecall.html',
      },
    ];

    // Score each knowledge base item by how many keywords match
    const isProblemQuery =
      /\b(slow|not working|down|broken|outage|no internet|cant|can't|cannot|help|issue|problem|error|buffering|lag)\b/.test(
        searchLower
      );
    const topicTokens = correctedWords.filter((w) => w.length > 2);

    knowledgeBase.forEach((item) => {
      const titleLower = item.title.toLowerCase();
      const contentLower = item.content.toLowerCase();
      const aliasLower = (item.keywords || []).join(' ').toLowerCase();
      let score = 0;

      // Exact full phrase match = highest score
      if (titleLower.includes(searchLower)) score += 10;
      if (contentLower.includes(searchLower)) score += 6;
      if (aliasLower.includes(searchLower)) score += 8;

      // Individual keyword matches
      allKeywords.forEach((kw) => {
        if (!kw) return;
        if (titleLower.includes(kw)) score += 4;
        if (contentLower.includes(kw)) score += 2;
        if (aliasLower.includes(kw)) score += 3;
      });

      // Strong bonus when the hit shares the user's topic words (tv, router, bill…)
      topicTokens.forEach((tok) => {
        if (titleLower.includes(tok)) score += 8;
        if (aliasLower.includes(tok)) score += 6;
      });

      // Prefer real help over marketing when someone has a problem
      if (isProblemQuery) {
        if (item.type === 'faq' || item.type === 'troubleshooting') score += 12;
        if (
          item.url === '/' ||
          item.url === '/plans' ||
          item.url === '/about' ||
          item.url === '/gamers' ||
          item.url === '/how-it-works'
        ) {
          score -= 25;
        }
      }

      // Topic guards — stop wrong FAQs winning
      if (topicIsTv) {
        if (/\b(tv|smart tv|streaming|netflix)\b/.test(titleLower + ' ' + aliasLower)) score += 30;
        if (/\bno internet connection\b/.test(titleLower) && !/\btv\b/.test(titleLower)) score -= 40;
        if (/\bhigh-speed internet plans\b/.test(titleLower)) score -= 50;
      }
      if (/\b(bill|payment|extend|invoice)\b/.test(searchLower) && /\b(bill|payment|extend|invoice)\b/.test(titleLower)) {
        score += 20;
      }

      if (score > 0) {
        results.push({
          type: item.type,
          title: item.title,
          content: item.content,
          ...(item.elementId && { elementId: item.elementId }),
          url: item.url,
          score,
        });
      }
    });

    // Dedupe by title+url, keep highest score
    const deduped = new Map<string, (typeof results)[number]>();
    results.forEach((r) => {
      const key = `${r.url || ''}|${r.title}`;
      const prev = deduped.get(key);
      if (!prev || r.score > prev.score) deduped.set(key, r);
    });
    const unique = Array.from(deduped.values());

    // One conversational answer — not a pile of search results
    unique.sort((a, b) => b.score - a.score);
    const bestRaw = unique[0];
    // Prefer visual guides over plain FAQ IDs when the topic is connection/router
    const best = bestRaw
      ? {
          ...bestRaw,
          ...(/\b(no internet|router|connection|poe)\b/i.test(bestRaw.title) &&
          (!bestRaw.elementId || bestRaw.elementId.startsWith('faq-'))
            ? { elementId: 'interactive' }
            : {}),
          ...(/\b(tv|smart tv|streaming)\b/i.test(bestRaw.title) &&
          (!bestRaw.elementId || bestRaw.elementId.startsWith('faq-'))
            ? { elementId: 'tv-troubleshooting' }
            : {}),
        }
      : undefined;
    setSearchResults(best ? [best] : []);
    if (!best) {
      setAssistantReply(noMatchReply(query));
    } else {
      window.AirmaxVisitor?.note(`Support ask: ${best.title.slice(0, 80)}`);
      setAssistantReply(hitToChatReply(query, best));
    }
    setIsLookingUp(false);
  };

  /** Immediate ask (suggestions, Enter, Ask button) */
  const handleSearch = (query: string) => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setSearchQuery(query);
    setIsLookingUp(Boolean(query.trim()));
    runAssistantLookup(query);
  };

  /** Type freely — answer after they pause (feels like a real chat) */
  const onSearchInputChange = (value: string) => {
    setSearchQuery(value);
    setCorrectedQuery('');
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    if (!value.trim()) {
      setAssistantReply(null);
      setSearchResults([]);
      setIsLookingUp(false);
      return;
    }
    setIsLookingUp(true);
    setAssistantReply(null);
    searchTimerRef.current = window.setTimeout(() => {
      runAssistantLookup(value);
    }, 700);
  };

  const scrollToResult = (result: { elementId?: string; url?: string }) => {
    const url = result.url || '';
    const isSupportPage = !url || url === '/support' || url === '/the';

    // External or other site pages open in a new tab
    if (url && !isSupportPage) {
      if (url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.assign(url);
      }
      return;
    }
    
    // Otherwise scroll to the element on current page
    if (!result.elementId) return;
    
    let element: HTMLElement | null = null;
    
    if (result.elementId === 'interactive') {
      element = interactiveSectionRef.current;
    } else if (result.elementId === 'antenna-status') {
      window.dispatchEvent(new Event('airmax-open-antenna-check'));
      return;
    } else if (result.elementId === 'troubleshooting') {
      element = troubleshootingSectionRef.current;
    } else if (result.elementId === 'tv-troubleshooting') {
      element = document.getElementById('tv-troubleshooting');
    } else if (result.elementId === 'faqs') {
      element = faqSectionRef.current;
    } else if (result.elementId.startsWith('faq-')) {
      // Find specific FAQ
      const faqIndex = parseInt(result.elementId.replace('faq-', ''));
      const faqElements = document.querySelectorAll('[data-faq-index]');
      element = faqElements[faqIndex] as HTMLElement;
    }

    if (element) {
      // Clear any previous highlight
      if (highlightedElement && highlightedElement !== element) {
        highlightedElement.style.backgroundColor = '';
        highlightedElement.style.borderLeft = '';
        highlightedElement.style.paddingLeft = '';
      }
      // Also auto-open FAQ if it's a faq element
      if (result.elementId && result.elementId.startsWith('faq-')) {
        const faqIndex = parseInt(result.elementId.replace('faq-', ''));
        setExpandedFaq(faqIndex);
      }
      setTimeout(() => {
        element!.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      // Bold highlight: yellow background + thick blue left border — stays until click or scroll
      element.style.transition = 'background-color 0.3s ease';
      element.style.backgroundColor = '#fef9c3';
      element.style.borderLeft = '5px solid #2563eb';
      element.style.paddingLeft = '12px';
      element.style.borderRadius = '6px';
      setHighlightedElement(element);

      // Clear highlight on next scroll or click anywhere
      const clearHighlight = () => {
        element!.style.backgroundColor = '';
        element!.style.borderLeft = '';
        element!.style.paddingLeft = '';
        setHighlightedElement(null);
        window.removeEventListener('scroll', clearHighlight);
        document.removeEventListener('click', clearHighlight);
      };
      setTimeout(() => {
        window.addEventListener('scroll', clearHighlight, { once: true });
        document.addEventListener('click', clearHighlight, { once: true });
      }, 500);
    }
  };

  const clearSearch = () => {
    handleSearch('');
    setAssistantReply(null);
  };

  const openGuideFromReply = () => {
    if (!assistantReply?.elementId) return;
    const elementId = assistantReply.elementId;
    clearSearch();
    setTimeout(() => scrollToResult({ elementId, url: '/support' }), 80);
  };

  /** Search / assistant mode — hide the rest of the page so the answer is obvious */
  const assistantActive = Boolean(searchQuery);

  useEffect(() => {
    if (!searchQuery) return;
    const audio = supportAudioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsSupportAudioPlaying(false);
    }
  }, [searchQuery]);

  // Mobile: free the screen while Interactive Connection Troubleshooting is in view
  useEffect(() => {
    const el = interactiveSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const mq = window.matchMedia('(max-width: 767px)');
    const update = (entry?: IntersectionObserverEntry) => {
      if (!mq.matches) {
        setHideMobileSearchForTroubleshoot(false);
        return;
      }
      if (!entry) return;
      setHideMobileSearchForTroubleshoot(entry.isIntersecting && entry.intersectionRatio >= 0.2);
    };

    const observer = new IntersectionObserver(
      (entries) => update(entries[0]),
      {
        threshold: [0, 0.15, 0.25, 0.4, 0.6],
        rootMargin: '-64px 0px -10% 0px',
      }
    );
    observer.observe(el);

    const onMq = () => {
      if (!mq.matches) setHideMobileSearchForTroubleshoot(false);
    };
    mq.addEventListener('change', onMq);

    return () => {
      observer.disconnect();
      mq.removeEventListener('change', onMq);
    };
  }, []);

  const toggleSupportAudio = async () => {
    const audio = supportAudioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        await audio.play();
        setIsSupportAudioPlaying(true);
      } else {
        audio.pause();
        setIsSupportAudioPlaying(false);
      }
    } catch {
      setIsSupportAudioPlaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section - collapses when searching */}
      <motion.section
        className="relative bg-gradient-to-br from-airmax-blue to-blue-600 overflow-hidden"
        animate={{
          height: searchQuery ? 0 : 'auto',
          paddingTop: searchQuery ? 0 : undefined,
          paddingBottom: searchQuery ? 0 : undefined,
          opacity: searchQuery ? 0 : 1,
        }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
      >
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Support Center
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                Our 24/7 Self-Service Support Center — troubleshooting, account access, billing, and payments anytime.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <audio
                  ref={supportAudioRef}
                  src="/audio/support-center.mp3"
                  preload="metadata"
                  onEnded={() => setIsSupportAudioPlaying(false)}
                  onPause={() => {
                    if (supportAudioRef.current?.ended === false && supportAudioRef.current?.paused) {
                      setIsSupportAudioPlaying(false);
                    }
                  }}
                  onPlay={() => setIsSupportAudioPlaying(true)}
                />
                <button
                  type="button"
                  onClick={() => void toggleSupportAudio()}
                  className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3.5 text-base font-semibold text-airmax-blue shadow-lg hover:bg-blue-50 transition-colors"
                  aria-pressed={isSupportAudioPlaying}
                >
                  {isSupportAudioPlaying ? (
                    <PauseCircleIcon className="h-7 w-7 flex-shrink-0" />
                  ) : (
                    <SpeakerWaveIcon className="h-7 w-7 flex-shrink-0" />
                  )}
                  {isSupportAudioPlaying ? 'Pause audio guide' : 'Listen to how Support works'}
                </button>
              </div>
              <p className="mt-3 text-sm text-blue-100/90 max-w-2xl mx-auto">
                Live Phone Support: Monday–Friday, 9 AM – 5 PM
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Search Bar - sticky; hidden on mobile while Interactive Troubleshooting is on screen */}
      <section
        className={`py-3 md:py-4 bg-white border-b sticky z-40 shadow-md transition-all duration-300 ${
          searchQuery ? 'top-16' : 'top-20'
        } ${hideMobileSearchForTroubleshoot && !searchQuery ? 'max-md:hidden' : ''}`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch(searchQuery);
                }
              }}
              placeholder="Type your question — I’ll answer"
              onContextMenu={(e) => e.stopPropagation()}
              className="block w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-airmax-blue focus:border-airmax-blue text-base"
            />
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                  type="button"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => handleSearch(searchQuery)}
                disabled={!searchQuery.trim()}
                className="rounded-lg bg-airmax-blue px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
              >
                Ask
              </button>
            </div>
          </div>
          {!searchQuery && (
            <p className="mt-2 text-sm text-gray-500 text-center md:text-left">
              Ask in plain words — like talking to support. Example: “my internet is slow”.
            </p>
          )}
          {/* Autocorrect suggestion */}
          {correctedQuery && (
            <p className="mt-2 text-sm text-gray-500">
              Searching for: <button onClick={() => handleSearch(correctedQuery)} className="text-airmax-blue font-semibold hover:underline">{correctedQuery}</button>
            </p>
          )}
        </div>
      </section>

      {/* Support Assistant — chat answer (one reply, not a results dump) */}
      {assistantActive && (
        <section className="bg-gray-50 min-h-[calc(100svh-8rem)] pb-10">
          <div className="max-w-4xl mx-auto px-4 pt-4 sm:px-6 lg:px-8">
            {isLookingUp && !assistantReply ? (
              <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-6">
                <div className="flex items-center gap-2 text-airmax-blue mb-4">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  <p className="text-sm font-semibold">Airmax Support Assistant</p>
                </div>
                <div className="flex justify-end mb-4">
                  <div className="max-w-[90%] rounded-2xl rounded-br-md bg-airmax-blue text-white px-4 py-2.5 text-sm">
                    {searchQuery.trim()}
                  </div>
                </div>
                <p className="text-gray-600 text-base">Looking that up for you…</p>
              </div>
            ) : assistantReply ? (
              <AssistantChatCard
                reply={assistantReply}
                query={searchQuery}
                onSuggestion={handleSearch}
                onOpenGuide={assistantReply.elementId ? openGuideFromReply : undefined}
                onClear={clearSearch}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center">
                <p className="text-gray-700 font-medium">No matches yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Try words like router, slow, WiFi, TV, or billing — or just say hello.
                </p>
                <a href="tel:928-565-6707" className="inline-block mt-4 text-airmax-blue font-semibold">
                  Or call 928-565-6707
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Page body — hidden while the assistant is answering */}
      <div className={assistantActive ? 'hidden' : ''}>
      {/* Quick Contact Options — compact icon grid (especially on mobile) */}
      <section className="py-6 md:py-10 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold text-gray-500 mb-4 md:mb-6">
            Quick help
          </p>

          {/* Mobile: tight icon grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:hidden">
            <a
              href="tel:928-565-6707"
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <PhoneIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Phone Support</span>
            </a>
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <EnvelopeIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Email Us</span>
            </Link>
            <Link
              to="/chat"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Chat from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <ChatBubbleLeftRightIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Live Chat</span>
            </Link>
            <a
              href="https://www.airmaxbilling.com/crm/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <DocumentTextIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Portal</span>
            </a>
            <Link
              to="/pay-bill"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Pay Bill from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <BanknotesIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Pay Bill</span>
            </Link>
            <a
              href="https://www.airmaxbilling.com/crm/reset-password"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.AirmaxVisitor?.note('Opened Billing Password Reset from Support')}
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <LockClosedIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">Reset Billing Password</span>
              <span className="text-[11px] text-gray-500 text-center leading-tight -mt-1 px-1">
                Portal only — not WiFi
              </span>
            </a>
            <Link
              to="/payment-extension"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Need More Time from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 border border-gray-100 px-2 py-4 active:bg-blue-50 sm:col-span-1 col-span-2"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-airmax-blue text-white">
                <CalendarDaysIcon className="h-6 w-6" />
              </span>
              <span className="text-xs font-semibold text-gray-900 text-center leading-tight">
                Need More Time
              </span>
              <span className="text-[11px] text-gray-500 text-center leading-tight -mt-1">
                Extend your bill due date
              </span>
            </Link>
          </div>

          {/* Desktop: same actions as compact icon cards with short labels */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <a
              href="tel:928-565-6707"
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <PhoneIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Phone Support</h3>
              <p className="text-sm text-airmax-blue font-semibold">928-565-6707</p>
              <p className="text-xs text-gray-500 mt-1">Mon–Fri 9 AM–5 PM (AZ)</p>
            </a>
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <EnvelopeIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
              <p className="text-sm text-gray-600">Send a message</p>
              <p className="text-xs text-gray-500 mt-1">Within 24h</p>
            </Link>
            <Link
              to="/chat"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Chat from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Live Chat</h3>
              <p className="text-sm text-gray-600">Chat with support</p>
              <p className="text-xs text-airmax-blue font-semibold mt-1">Start chat</p>
            </Link>
            <a
              href="https://www.airmaxbilling.com/crm/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <DocumentTextIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Portal</h3>
              <p className="text-sm text-gray-600">Manage account</p>
              <p className="text-xs text-airmax-blue font-semibold mt-1">Login</p>
            </a>
            <Link
              to="/pay-bill"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Pay Bill from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <BanknotesIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Pay Bill</h3>
              <p className="text-sm text-gray-600">Phone verify</p>
              <p className="text-xs text-airmax-blue font-semibold mt-1">Pay now</p>
            </Link>
            <a
              href="https://www.airmaxbilling.com/crm/reset-password"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.AirmaxVisitor?.note('Opened Billing Password Reset from Support')}
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <LockClosedIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Reset Billing Password</h3>
              <p className="text-sm text-gray-600">Billing portal only</p>
              <p className="text-xs text-gray-500 mt-1">Not your WiFi password</p>
            </a>
            <Link
              to="/payment-extension"
              onClick={() => {
                window.AirmaxVisitor?.note('Opened Need More Time from Support');
                window.scrollTo(0, 0);
              }}
              className="flex flex-col items-center text-center p-5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
            >
              <CalendarDaysIcon className="h-8 w-8 text-airmax-blue mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Need More Time</h3>
              <p className="text-sm text-gray-600">Extend your bill due date</p>
            </Link>
          </div>

          {/* Extra tools — long rows on mobile, compact cards in one row on desktop */}
          <div className="mt-5 md:mt-8 flex flex-col gap-3 md:grid md:grid-cols-3 md:gap-4">
            <a
              href="https://airmaxcloud.com/app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-blue-50 px-4 py-3 hover:border-airmax-blue transition-colors md:flex-col md:items-center md:justify-center md:gap-3 md:px-4 md:py-5 md:text-center"
            >
              <span className="flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-xl bg-airmax-blue text-white flex-shrink-0">
                <DevicePhoneMobileIcon className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1 md:flex-none text-left md:text-center">
                <p className="font-semibold text-gray-900 text-sm md:text-base">Mobile App</p>
                <p className="text-xs text-gray-600 truncate md:whitespace-normal md:mt-1">
                  <span className="md:hidden">Payments, account &amp; service info in your browser</span>
                  <span className="hidden md:inline">Pay &amp; manage account</span>
                </p>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-airmax-blue flex-shrink-0 md:hidden" />
            </a>

            <a
              href="https://www.trendmicro.com/en_ae/forHome/products/housecall.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.AirmaxVisitor?.note('Opened Trend Micro HouseCall from Support')}
              className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-emerald-50 px-4 py-3 hover:border-emerald-600 transition-colors md:flex-col md:items-center md:justify-center md:gap-3 md:px-4 md:py-5 md:text-center"
            >
              <span className="flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-xl bg-emerald-600 text-white flex-shrink-0">
                <ShieldCheckIcon className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1 md:flex-none text-left md:text-center">
                <p className="font-semibold text-gray-900 text-sm md:text-base">Free Virus Scan</p>
                <p className="text-xs text-gray-600 truncate md:whitespace-normal md:mt-1">
                  <span className="md:hidden">Scan your computer for free with Trend Micro HouseCall</span>
                  <span className="hidden md:inline">Trend Micro HouseCall</span>
                </p>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-emerald-700 flex-shrink-0 md:hidden" />
            </a>

            <AntennaStatusChecker />
          </div>
        </div>
      </section>

      {/* Interactive Visual Troubleshooting */}
      <section
        id="interactive-troubleshooting"
        ref={interactiveSectionRef}
        className={`bg-gradient-to-br from-blue-50 to-gray-50 ${
          hideMobileSearchForTroubleshoot ? 'py-4 md:py-20' : 'py-12 md:py-20'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Interactive Connection Troubleshooting
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
                Follow our step-by-step visual guide to check your connection
              </p>
              <p className="text-lg text-airmax-blue font-semibold">
                Easy to follow • No technical knowledge required
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <InteractiveTroubleshooting />
          </motion.div>
        </div>
      </section>

      {/* Smart TV Troubleshooting Section */}
      <section id="tv-troubleshooting" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <TVTroubleshooting />
          </motion.div>
        </div>
      </section>

      {/* Troubleshooting Guides */}
      <section ref={troubleshootingSectionRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Issues & Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find quick solutions to the most common internet problems.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {troubleshootingSteps.map((guide, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <guide.icon className="h-6 w-6 text-airmax-blue mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{guide.title}</h3>
                </div>
                <ol className="space-y-3">
                  {guide.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start">
                      <span className="bg-airmax-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5 flex-shrink-0">
                        {stepIndex + 1}
                      </span>
                      <button
                        onClick={() => {
                          const q = stepToSearchQuery(step);
                          handleSearch(q);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-gray-600 text-sm text-left hover:text-airmax-blue hover:underline cursor-pointer group flex items-center gap-1"
                      >
                        {step}
                        <ArrowRightIcon className="h-3.5 w-3.5 text-airmax-blue opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </button>
                    </li>
                  ))}
                </ol>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqSectionRef} className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 cursor-default select-none"
              onClick={handleAdminTitleClick}
              title=""
            >
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our service.
            </p>
            {isFaqAdmin && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdminPanel(true);
                    void loadPendingSuggestions();
                  }}
                  className="inline-flex items-center rounded-lg bg-airmax-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Open FAQ Admin Panel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingIndex(null);
                    setNewFaqQ('');
                    setNewFaqA('');
                    setShowAdminPanel(true);
                  }}
                  className="inline-flex items-center rounded-lg border border-airmax-blue px-4 py-2 text-sm font-semibold text-airmax-blue hover:bg-blue-50"
                >
                  + Add FAQ
                </button>
                <span className="text-xs text-gray-500">Admin mode — edits save for all visitors</span>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={`${index}-${faq.question.slice(0, 24)}`}
                data-faq-index={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
                className="bg-gray-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <QuestionMarkCircleIcon className="h-5 w-5 text-airmax-blue mr-3 flex-shrink-0" />
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                  </div>
                  {expandedFaq === index ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4"
                  >
                    <p className="text-gray-600 pl-8 whitespace-pre-line">{renderFaqAnswer(faq.answer)}</p>
                    {isFaqAdmin && (
                      <div className="mt-4 pl-8 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditFaq(index)}
                          className="text-xs px-3 py-1.5 bg-blue-100 text-airmax-blue rounded font-medium hover:bg-blue-200"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFaq(index)}
                          className="text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded font-medium hover:bg-red-200"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveFaq(index, -1)}
                          disabled={index === 0}
                          className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                        >
                          Move up
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveFaq(index, 1)}
                          disabled={index === faqs.length - 1}
                          className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                        >
                          Move down
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Client FAQ suggestion */}
          <div className="mt-10 rounded-2xl border border-gray-200 bg-gray-50 p-5 md:p-6">
            {!showSuggestForm ? (
              <div className="text-center">
                <p className="text-gray-800 font-semibold">Don’t see your question?</p>
                <p className="text-sm text-gray-600 mt-1 mb-4">
                  Send it to us and we may add it to the FAQ list.
                </p>
                <button
                  type="button"
                  onClick={() => void openSuggestForm()}
                  className="inline-flex items-center rounded-xl bg-airmax-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Suggest a new FAQ
                </button>
              </div>
            ) : (
              <form onSubmit={submitFaqSuggestion} className="space-y-4 max-w-xl mx-auto relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Suggest a new FAQ</h3>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Tell us what you’d like answered. We’ll review it before it goes live.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSuggestForm(false);
                      setSuggestError('');
                      setSuggestSuccess('');
                    }}
                    className="text-sm font-medium text-gray-500 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>

                <div>
                  <label htmlFor="faq-suggest-q" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Your question
                  </label>
                  <textarea
                    id="faq-suggest-q"
                    value={suggestQuestion}
                    onChange={(e) => setSuggestQuestion(e.target.value)}
                    rows={3}
                    required
                    minLength={8}
                    maxLength={500}
                    placeholder="Example: How do I change my Wi-Fi password?"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="faq-suggest-name" className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Name <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="faq-suggest-name"
                      type="text"
                      value={suggestName}
                      onChange={(e) => setSuggestName(e.target.value)}
                      maxLength={120}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                  </div>
                  <div>
                    <label htmlFor="faq-suggest-email" className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Email <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="faq-suggest-email"
                      type="email"
                      value={suggestEmail}
                      onChange={(e) => setSuggestEmail(e.target.value)}
                      maxLength={200}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                  </div>
                </div>

                {/* Honeypot — hidden from people */}
                <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="faq-suggest-website">Website</label>
                  <input
                    id="faq-suggest-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={suggestHoneypot}
                    onChange={(e) => setSuggestHoneypot(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="faq-suggest-captcha" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Security check
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    {captchaPrompt || 'Loading math problem…'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id="faq-suggest-captcha"
                      type="text"
                      inputMode="numeric"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      required
                      placeholder="Your answer"
                      className="w-36 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                    <button
                      type="button"
                      onClick={() => void loadCaptcha()}
                      className="text-sm font-medium text-airmax-blue hover:underline"
                    >
                      New problem
                    </button>
                  </div>
                </div>

                {suggestError && <p className="text-sm text-red-600">{suggestError}</p>}
                {suggestSuccess && <p className="text-sm text-green-700">{suggestSuccess}</p>}

                <button
                  type="submit"
                  disabled={suggestLoading || !suggestQuestion.trim() || !captchaAnswer || !captchaToken}
                  className="w-full sm:w-auto rounded-xl bg-airmax-blue px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {suggestLoading ? 'Sending…' : 'Submit question'}
                </button>
              </form>
            )}
          </div>

          {/* Support Center improvement feedback */}
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
            {!showCenterFeedbackForm ? (
              <div className="text-center">
                <p className="text-gray-800 font-semibold">Help us improve the Support Center</p>
                <p className="text-sm text-gray-600 mt-1 mb-4">
                  Tell us what you like, what you don’t, and what you’d like to see here.
                </p>
                <button
                  type="button"
                  onClick={() => void openCenterFeedbackForm()}
                  className="inline-flex items-center rounded-xl border border-airmax-blue bg-white px-5 py-2.5 text-sm font-semibold text-airmax-blue hover:bg-blue-50"
                >
                  Share feedback
                </button>
              </div>
            ) : (
              <form onSubmit={submitCenterFeedback} className="space-y-4 max-w-xl mx-auto relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Support Center feedback</h3>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Your notes help us make this page clearer and more useful.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCenterFeedbackForm(false);
                      setCenterError('');
                      setCenterSuccess('');
                    }}
                    className="text-sm font-medium text-gray-500 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>

                <div>
                  <label htmlFor="center-liked" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    What do you like?
                  </label>
                  <textarea
                    id="center-liked"
                    value={centerLiked}
                    onChange={(e) => setCenterLiked(e.target.value)}
                    rows={2}
                    maxLength={1200}
                    placeholder="Example: The troubleshooting steps are easy to follow."
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  />
                </div>

                <div>
                  <label htmlFor="center-disliked" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    What don’t you like / what’s confusing?
                  </label>
                  <textarea
                    id="center-disliked"
                    value={centerDisliked}
                    onChange={(e) => setCenterDisliked(e.target.value)}
                    rows={2}
                    maxLength={1200}
                    placeholder="Example: It’s hard to find billing help."
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  />
                </div>

                <div>
                  <label htmlFor="center-ideas" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    What would you like to see here?
                  </label>
                  <textarea
                    id="center-ideas"
                    value={centerIdeas}
                    onChange={(e) => setCenterIdeas(e.target.value)}
                    rows={2}
                    maxLength={1200}
                    placeholder="Example: More video guides, outage map, Spanish FAQs…"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="center-name" className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Name <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="center-name"
                      type="text"
                      value={centerName}
                      onChange={(e) => setCenterName(e.target.value)}
                      maxLength={120}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                  </div>
                  <div>
                    <label htmlFor="center-email" className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Email <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="center-email"
                      type="email"
                      value={centerEmail}
                      onChange={(e) => setCenterEmail(e.target.value)}
                      maxLength={200}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                  </div>
                </div>

                <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="center-website">Website</label>
                  <input
                    id="center-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={centerHoneypot}
                    onChange={(e) => setCenterHoneypot(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="center-captcha" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Security check
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    {centerCaptchaPrompt || 'Loading math problem…'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id="center-captcha"
                      type="text"
                      inputMode="numeric"
                      value={centerCaptchaAnswer}
                      onChange={(e) => setCenterCaptchaAnswer(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      required
                      placeholder="Your answer"
                      className="w-36 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                    />
                    <button
                      type="button"
                      onClick={() => void loadCenterCaptcha()}
                      className="text-sm font-medium text-airmax-blue hover:underline"
                    >
                      New problem
                    </button>
                  </div>
                </div>

                {centerError && <p className="text-sm text-red-600">{centerError}</p>}
                {centerSuccess && <p className="text-sm text-green-700">{centerSuccess}</p>}

                <button
                  type="submit"
                  disabled={
                    centerLoading
                    || !(centerLiked.trim() || centerDisliked.trim() || centerIdeas.trim())
                    || !centerCaptchaAnswer
                    || !centerCaptchaToken
                  }
                  className="w-full sm:w-auto rounded-xl bg-airmax-blue px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {centerLoading ? 'Sending…' : 'Submit feedback'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Admin Access</h3>
            <p className="text-sm text-gray-500 mb-4">Enter your admin password to manage FAQs.</p>
            <input
              type="password"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
              placeholder="Admin password"
              className="block w-full px-4 py-2 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-airmax-blue"
              autoFocus
            />
            {adminError && <p className="text-red-500 text-sm mb-3">{adminError}</p>}
            <div className="flex gap-3">
              <button onClick={handleAdminLogin} className="flex-1 bg-airmax-blue text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Login</button>
              <button onClick={() => setShowPasswordModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin FAQ Panel */}
      {showAdminPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-xl font-bold text-gray-900">FAQ Admin Panel</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Add, edit, reorder, or delete — changes save instantly for everyone
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAdminPanel(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close admin panel"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4">
              {(faqSaveStatus || faqSaving) && (
                <p className={`mb-4 text-sm font-medium ${faqSaving ? 'text-gray-500' : 'text-green-700'}`}>
                  {faqSaveStatus || 'Saving…'}
                </p>
              )}

              {/* Add / Edit form */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-airmax-blue mb-3">
                  {editingIndex !== null ? `Edit FAQ #${editingIndex + 1}` : 'Add New FAQ'}
                </h4>
                <input
                  type="text"
                  value={newFaqQ}
                  onChange={e => setNewFaqQ(e.target.value)}
                  placeholder="Question (e.g. How do I reset my password?)"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                />
                <textarea
                  value={newFaqA}
                  onChange={e => setNewFaqA(e.target.value)}
                  placeholder="Answer..."
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue resize-y"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddFaq}
                    disabled={!newFaqQ.trim() || !newFaqA.trim() || faqSaving}
                    className="flex-1 bg-airmax-blue text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  >
                    {editingIndex !== null ? 'Save Changes' : 'Add FAQ'}
                  </button>
                  {editingIndex !== null && (
                    <button
                      type="button"
                      onClick={() => { setEditingIndex(null); setNewFaqQ(''); setNewFaqA(''); }}
                      className="px-4 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Customer-submitted FAQ requests */}
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold text-amber-900">
                    Customer requests ({pendingSuggestions.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => void loadPendingSuggestions()}
                    disabled={suggestAdminBusy}
                    className="text-xs font-semibold text-amber-800 hover:underline disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>
                {pendingSuggestions.length === 0 ? (
                  <p className="text-sm text-amber-800/70">No pending FAQ suggestions.</p>
                ) : (
                  <div className="space-y-3">
                    {pendingSuggestions.map((s) => (
                      <div key={s.id} className="rounded-lg border border-amber-200 bg-white p-3">
                        <p className="text-sm font-semibold text-gray-900">{s.question}</p>
                        {(s.name || s.email) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {[s.name, s.email].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <textarea
                          value={approveAnswer[s.id] || ''}
                          onChange={(e) =>
                            setApproveAnswer((prev) => ({ ...prev, [s.id]: e.target.value }))
                          }
                          rows={2}
                          placeholder="Write the FAQ answer to publish…"
                          className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => void approveSuggestion(s.id)}
                            disabled={suggestAdminBusy || !(approveAnswer[s.id] || '').trim()}
                            className="text-xs px-3 py-1.5 bg-airmax-blue text-white rounded font-medium hover:bg-blue-700 disabled:opacity-40"
                          >
                            Publish FAQ
                          </button>
                          <button
                            type="button"
                            onClick={() => void dismissSuggestion(s.id)}
                            disabled={suggestAdminBusy}
                            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support Center improvement feedback */}
              <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold text-sky-900">
                    Support Center feedback ({pendingCenterFeedback.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => void loadPendingCenterFeedback()}
                    disabled={suggestAdminBusy}
                    className="text-xs font-semibold text-sky-800 hover:underline disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>
                {pendingCenterFeedback.length === 0 ? (
                  <p className="text-sm text-sky-800/70">No pending Support Center feedback.</p>
                ) : (
                  <div className="space-y-3">
                    {pendingCenterFeedback.map((f) => (
                      <div key={f.id} className="rounded-lg border border-sky-200 bg-white p-3 space-y-2">
                        {f.liked ? (
                          <p className="text-sm text-gray-800">
                            <span className="font-semibold text-gray-900">Like: </span>
                            {f.liked}
                          </p>
                        ) : null}
                        {f.disliked ? (
                          <p className="text-sm text-gray-800">
                            <span className="font-semibold text-gray-900">Don’t like: </span>
                            {f.disliked}
                          </p>
                        ) : null}
                        {f.ideas ? (
                          <p className="text-sm text-gray-800">
                            <span className="font-semibold text-gray-900">Want to see: </span>
                            {f.ideas}
                          </p>
                        ) : null}
                        {(f.name || f.email) && (
                          <p className="text-xs text-gray-500">
                            {[f.name, f.email].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => void dismissCenterFeedback(f.id)}
                          disabled={suggestAdminBusy}
                          className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                        >
                          Dismiss
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* All FAQs list */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  All FAQs ({faqs.length})
                </h4>
                {faqs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No FAQs yet. Add your first one above.</p>
                ) : (
                  faqs.map((faq, i) => (
                    <div
                      key={i}
                      className={`bg-gray-50 border rounded-lg p-4 ${editingIndex === i ? 'border-airmax-blue ring-1 ring-airmax-blue' : 'border-gray-200'}`}
                    >
                      <p className="font-semibold text-gray-900 text-sm mb-1">
                        <span className="text-gray-400 mr-2">{i + 1}.</span>
                        {faq.question}
                      </p>
                      <p className="text-gray-500 text-xs mb-3 line-clamp-3 whitespace-pre-line">{faq.answer}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditFaq(i)}
                          className="text-xs px-3 py-1 bg-blue-100 text-airmax-blue rounded font-medium hover:bg-blue-200"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFaq(i)}
                          className="text-xs px-3 py-1 bg-red-100 text-red-600 rounded font-medium hover:bg-red-200"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveFaq(i, -1)}
                          disabled={i === 0 || faqSaving}
                          className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveFaq(i, 1)}
                          disabled={i === faqs.length - 1 || faqSaving}
                          className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded font-medium hover:bg-gray-200 disabled:opacity-40"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Support;
